import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

const resources = {
  en: {
    translation: {
      nav: {
        home: "Home",
        lessons: "Theory Lessons",
        trafficSigns: "Traffic Signs",
        tests: "Practice Tests",
        videos: "Video Lessons",
        assistant: "AI Assistant",
        support: "Support",
        pricing: "Pricing",
        login: "Login",
        register: "Register",
        logout: "Logout",
        dashboard: "Dashboard"
      },
      hero: {
        title: "Pass Your Driving Theory In Your Language",
        subtitle: "The only platform with 9 languages for the Swedish B driving license. Practice tests to prepare you for the theory test (kunskapsprov), interactive lessons, and an AI assistant available 24/7.",
        confidence: "B License Theory",
        startTrial: "Get Free Questions",
        learnMore: "Learn More",
        trustedPlatform: "Trusted Learning Platform",
        bLicense: "B License",
        languageBadge: "Available in 9 languages",
        studentsCount: "Join 800+ students"
      },
      howItWorks: {
        badge: "Simple & Effective",
        title: "3 Steps to Pass Your Theory Test",
        step1Title: "Sign Up & Start Free",
        step1Desc: "Create your account and try free questions — no payment required.",
        step2Title: "Practice in Your Language",
        step2Desc: "Study theory lessons and take practice tests in the language you understand best.",
        step3Title: "Pass the Theory Test",
        step3Desc: "Our questions help you prepare for the theory test (kunskapsprov), available in 9 languages for your convenience."
      },
      features: {
        title: "Everything You Need to Pass",
        whyChooseUs: "Why Choose Us",
        explore: "Explore",
        lessons: {
          title: "Theory Lessons",
          desc: "Comprehensive lessons covering all traffic rules, signs, and safety."
        },
        tests: {
          title: "Practice Tests",
          desc: "Hundreds of questions to prepare you for the theory test (kunskapsprov)."
        },
        videos: {
          title: "Video Tutorials",
          desc: "Visual learning with professional driving instructors."
        },
        ai: {
          title: "AI Assistant",
          desc: "Get instant answers to your driving questions 24/7."
        }
      },
      pricing: {
        title: "Simple, Transparent Pricing",
        subtitle: "Start with free questions. Subscribe for unlimited access.",
        monthly: "Monthly",
        sixMonths: "6 Months",
        perMonth: "kr/month",
        perYear: "kr",
        save: "Save 48%",
        features: [
          "All theory lessons",
          "Unlimited practice tests",
          "Video tutorials",
          "AI assistant",
          "Multi-language support"
        ],
        subscribe: "Start Subscription",
        trial: "Free questions, then",
        popular: "Popular",
        free: "Free",
        startFree: "Start Free",
        yourCurrentPlan: "Your current plan"
      },
      cta: {
        startJourney: "Start Your Journey to Success Today",
        joinStudents: "Join thousands of students who have passed their driving tests with our help"
      },
      auth: {
        login: "Login",
        register: "Create Account",
        email: "Email",
        password: "Password",
        name: "Full Name",
        confirmPassword: "Confirm Password",
        forgotPassword: "Forgot password?",
        noAccount: "Don't have an account?",
        hasAccount: "Already have an account?",
        loginBtn: "Sign In",
        registerBtn: "Create Account",
        or: "or",
        googleSignIn: "Sign in with Google",
        googleSignUp: "Sign up with Google",
        freeTrialQuestions: "Start free with practice questions",
        passwordMismatch: "Passwords do not match",
        passwordTooShort: "Password must be at least 6 characters",
        pleaseWait: "Please wait"
      },
      dashboard: {
        welcome: "Welcome back",
        subscription: "Subscription",
        trial: "Free Trial",
        active: "Active",
        expired: "Expired",
        daysLeft: "days left",
        upgrade: "Upgrade Now",
        continueLearning: "Continue Learning",
        quickActions: "Quick Actions"
      },
      // Dashboard flat keys (underscore format)
      dashboard_welcome: "Welcome back",
      dashboard_subscription: "Subscription",
      dashboard_trial: "Free Trial",
      dashboard_active: "Active",
      dashboard_expired: "Expired",
      dashboard_days_left: "days left",
      dashboard_upgrade: "Upgrade Now",
      dashboard_continue_learning: "Continue Learning",
      dashboard_quick_actions: "Quick Actions",
      dashboard_lessons_title: "Theory Lessons",
      dashboard_lessons_desc: "Learn traffic rules and signs",
      dashboard_tests_title: "Practice Tests",
      dashboard_tests_desc: "Test your knowledge",
      dashboard_videos_title: "Video Lessons",
      dashboard_videos_desc: "Watch and learn",
      dashboard_assistant_title: "AI Assistant",
      dashboard_assistant_desc: "Ask any question",
      dashboard_super_admin: "Super Admin",
      dashboard_admin: "Admin",
      dashboard_start_now: "Start Now",
      dashboard_read_more: "Read More",
      dashboard_subtitle: "Here's your learning progress",
      // Lessons flat keys
      lessons_title: "Theory Lessons",
      lessons_subtitle: "Learn traffic rules and road safety",
      lessons_no_lessons: "No lessons available yet",
      lessons_loading: "Loading lessons...",
      lessons_completed: "Completed",
      lessons_free: "Free",
      lessons_premium: "Premium",
      lessons_locked: "Locked",
      lessons_minutes: "minutes",
      lessons_read_lesson: "Read Lesson",
      // Tests flat keys
      tests_questions: "questions",
      tests_minutes: "minutes",
      // Videos flat keys
      videos_all_categories: "All Categories",
      videos_count: "videos",
      videos_free: "Free",
      videos_locked: "Locked",
      videos_loading: "Loading videos...",
      videos_minutes: "minutes",
      videos_title: "🎬 Tutorial Videos",
      videos_subtitle: "Watch tutorial videos to learn how to drive safely",
      videos_search: "Search...",
      videos_new: "New",
      videos_no_results: "😔 No videos found",
      videos_trending: "Trending",
      videos_views: "views",
      videos_watch: "Watch",
      videos_watched: "Watched",
      videos_subscription_required: "Subscription required",
      videos_scroll_more: "Scroll down for more",
      videos_total: "Total",
      videos_for_subscribers: "Videos for Subscribers",
      videos_subscribe_to_watch: "Subscribe to watch all videos!",
      videos_subscribe_now: "Subscribe Now 🚀",
      videos_later: "Later",
      // Settings flat keys
      settings_expires: "Expires",
      settings_title: "Settings",
      settings_account: "Account",
      settings_language: "Language",
      settings_notifications: "Notifications",
      settings_save: "Save",
      settings_saved: "Saved",
      settings_trialEnds: "Trial ends",
      // Subscription flat keys
      subscription_neverExpires: "Lifetime subscription - never expires ✨",
      // Support flat keys
      support_title: "Support",
      support_subtitle: "We're here to help",
      support_form_title: "Send us a message",
      support_form_subtitle: "We'll respond within 24 hours",
      support_name: "Your Name",
      support_email: "Your Email",
      support_subject: "Subject",
      support_message: "Message",
      support_send: "Send Message",
      support_sending: "Sending...",
      support_thank_you: "Thank you!",
      support_success_msg: "Your message has been sent successfully",
      support_send_another: "Send another message",
      support_faq_title: "Frequently Asked Questions",
      lessons: {
        title: "Theory Lessons",
        categories: {
          traffic_signs: "Traffic Signs",
          road_rules: "Road Rules",
          safety: "Safety",
          parking: "Parking"
        },
        readMore: "Read More"
      },
      lessons_category_traffic_rules: "Traffic Rules",
      lessons_category_traffic_signs: "Traffic Signs",
      lessons_category_road_rules: "Road Rules",
      lessons_category_safety: "Safety",
      lessons_category_parking: "Parking",
      lessons_category_general: "General",
      lessons_category_theory: "Theory",
      tests: {
        title: "Practice Tests",
        startTest: "Start Test",
        question: "Question",
        of: "of",
        submit: "Submit Test",
        next: "Next",
        previous: "Previous",
        results: "Test Results",
        score: "Your Score",
        correct: "Correct Answers",
        incorrect: "Incorrect",
        tryAgain: "Try Again",
        passed: "Congratulations! You passed!",
        failed: "Keep practicing. You can do it!"
      },
      videos: {
        title: "Video Lessons",
        duration: "Duration",
        watch: "Watch Now"
      },
      subscription: {
        neverExpires: "Lifetime subscription - never expires ✨",
        active: "Active",
        expired: "Expired",
        trial: "Trial",
        lifetime: "Lifetime"
      },
      settings: {
        expires: "Expires",
        trialEnds: "Trial ends",
        title: "Settings",
        account: "Account",
        language: "Language"
      },
      assistant: {
        title: "AI Driving Assistant",
        placeholder: "Ask me anything about driving...",
        send: "Send",
        thinking: "Thinking..."
      },
      mistakes: {
        commonMistakes: "Common Mistakes"
      },
      support: {
        title: "Support",
        subtitle: "We're here to help! Send us a message and we'll get back to you as soon as possible.",
        form: {
          title: "Send us a message",
          subtitle: "We'll respond within 24 hours"
        },
        contact: {
          email: { en: "Email", ar: "البريد الإلكتروني", sv: "E-post" },
          phone: { en: "Phone", ar: "الهاتف", sv: "Telefon" },
          hours: { en: "Support Hours", ar: "ساعات الدعم", sv: "Supporttider" }
        },
        faq: {
          title: "Frequently Asked Questions",
          questions: {
            howToStart: {
              question: "How do I start using the platform?",
              answer: "Simply register for an account and you'll get free questions to try out our platform."
            },
            subscriptionPlans: {
              question: "What subscription plans are available?",
              answer: "We offer 3 plans: 1 month, 3 months and 6 months with unlimited access to all content. One-time payment – no automatic renewal."
            },
            languageChange: {
              question: "How can I change the language?",
              answer: "Use the language selector in the top navigation menu to switch between languages."
            }
          }
        }
      },
      footer: {
        rights: "All rights reserved",
        privacy: "Privacy Policy",
        terms: "Terms of Service"
      },
      common: {
        loading: "Loading...",
        error: "An error occurred",
        success: "Success",
        cancel: "Cancel",
        save: "Save",
        back: "Back",
        delete: "Delete",
        edit: "Edit",
        add: "Add",
        search: "Search",
        refresh: "Refresh",
        actions: "Actions",
        filter: "Filter",
        next: "Next",
        previous: "Previous",
        submit: "Submit",
        close: "Close",
        confirm: "Confirm",
        redirecting: "Redirecting...",
        returningToLogin: "Returning to login..."
      },
      admin: {
        dataRefreshed: "Data refreshed successfully",
        welcome: "Welcome back, Admin!",
        welcomeSubtitle: "Here's what's happening with your driving school today.",
        menu: {
          dashboard: "Dashboard",
          users: "Users",
          lessons: "Lessons",
          questions: "Questions",
          videos: "Videos",
          payments: "Payments",
          settings: "Settings",
          backup: "Backup"
        },
        stats: {
          totalUsers: "Total Users",
          activeSubscribers: "Active Subscribers",
          monthlyRevenue: "Monthly Revenue",
          contentItems: "Content Items",
          users: "Users",
          active: "Active",
          trial: "Trial",
          lessons: "Lessons",
          questions: "Questions",
          videos: "Videos",
          payments: "Payments"
        },
        title: "Admin Dashboard",
        subtitle: "Manage your driving school platform",
        tabs: {
          users: "Users",
          lessons: "Lessons",
          questions: "Questions",
          videos: "Videos",
          payments: "Payments"
        },
        users: {
          searchPlaceholder: "Search users...",
          name: "Name",
          email: "Email",
          status: "Status",
          plan: "Plan",
          editSubscription: "Edit Subscription",
          daysToAdd: "Days to Add"
        },
        content: {
          addLesson: "Add Lesson",
          addQuestion: "Add Question",
          addVideo: "Add Video",
          titleEn: "Title (EN)",
          titleAr: "Title (AR)",
          titleSv: "Title (SV)",
          contentEn: "Content (EN)",
          contentAr: "Content (AR)",
          contentSv: "Content (SV)",
          category: "Category",
          imageUrl: "Image URL",
          order: "Order",
          options: "Options",
          correctAnswer: "Correct Answer",
          explanation: "Explanation",
          videoUrl: "Video URL",
          thumbnailUrl: "Thumbnail URL",
          duration: "Duration (seconds)",
          totalItems: "total items",
          noLessons: "No lessons yet. Add your first lesson!",
          noQuestions: "No questions yet. Add your first question!",
          noVideos: "No videos yet. Add your first video!",
          noPayments: "No payments recorded yet."
        },
        payments: {
          date: "Date",
          amount: "Amount"
        },
        export: {
          csv: "Export CSV",
          success: "Data exported successfully!"
        },
        filters: {
          all: "All",
          clear: "Clear Filters",
          results: "results"
        },
        delete: {
          confirm: "Are you sure? This action cannot be undone.",
          confirmUser: "Are you sure you want to delete {{name}}? This action cannot be undone.",
          userSuccess: "User deleted successfully",
          userError: "Failed to delete user",
          success: "Deleted successfully",
          error: "Failed to delete"
        },
        ban: {
          title: "Ban User",
          confirm: "Are you sure you want to ban {{name}}?",
          success: "User banned successfully",
          error: "Failed to ban user"
        },
        unban: {
          title: "Unban User",
          success: "User unbanned successfully",
          error: "Failed to unban user"
        },
        status: {
          banned: "Banned"
        },
        subscription: {
          updated: "Subscription updated successfully",
          error: "Failed to update subscription"
        },
        save: {
          error: "Failed to save changes"
        },
        create: {
          success: "Created successfully",
          lesson: "Create New Lesson",
          question: "Create New Question",
          video: "Create New Video"
        },
        update: {
          success: "Updated successfully"
        },
        edit: {
          lesson: "Edit Lesson",
          question: "Edit Question",
          video: "Edit Video"
        },
        manage: {
          users: "Manage all registered users, subscriptions, and permissions"
        }
      },
      resetPassword: {
        title: "Reset Password",
        subtitle: "Enter your email to receive reset instructions",
        sendLink: "Send Reset Link",
        backToLogin: "Back to Login",
        emailSent: "Password reset link sent to your email",
        newPassword: "New Password",
        confirmNewPassword: "Confirm New Password",
        resetSuccess: "Password reset successfully"
      },
      // Global UI translations
      ui: {
        overview: "Overview",
        theoryLessons: "Theory Lessons",
        trafficSigns: "Traffic Signs",
        practiceTests: "Practice Tests",
        quickTips: "Quick Tips",
        importantWords: "Important Words",
        howToUse: "How to Use",
        reviews: "Reviews",
        aiAssistant: "AI Assistant",
        prices: "Prices",
        settings: "Settings",
        logout: "Logout",
        login: "Login",
        register: "Register",
        subscribersOnly: "Subscribers Only Content",
        subscribeNow: "Subscribe Now",
        backToFreeTests: "Back to Free Tests",
        unlimitedQuestions: "Unlimited questions",
        allTheoryLessons: "All theory lessons",
        tutorialVideos: "AI assistant",
        subscribeDesc: "Subscribe now to access all lessons, traffic signs, and more!",
        currentPlan: "Current Plan",
        sixMonths: "6 Months",
        perMonth: "/month",
        profile: "Profile",
        subscribed: "Subscribed",
        trial: "Trial",
        subscribe: "Subscribe",
        free: "Free",
        startTest: "Start Test",
        forSubscribers: "For Subscribers",
        subscribeForAccess: "Subscribe for full access!",
        later: "Later",
        learnTrafficSigns: "Learn traffic signs",
        goldenTips: "Golden tips for the test",
        demoAccount: "Demo Account",
        demoFullAccess: "Demo Account - Full Access",
        timeRemaining: "Time remaining",
        accountExpired: "Account has expired",
        enjoyFeatures: "Enjoy all features!",
        upgradeNow: "Upgrade Now",
        renewNow: "Renew Now",
        renewSubscription: "Renew Subscription Now",
        requestRefund: "Request Refund",
        refundUnderReview: "Refund request under review",
        refundContactSoon: "We will contact you soon regarding your request.",
        refundReason: "Reason for refund request",
        explainRefund: "Please explain why you are requesting a refund...",
        submitRequest: "Submit Request",
        submitting: "Submitting...",
        refundAlreadyRequested: "Refund already requested",
        noActiveSubscription: "No active subscription to refund",
        refundRequestSuccess: "Your refund request has been submitted successfully"
      },
      footer: {
        privacy: "Privacy",
        terms: "Terms",
        refund: "Refund",
        disclaimer: "Disclaimer",
        cookies: "Cookies"
      }
    }
  },
  ar: {
    translation: {
      nav: {
        home: "الرئيسية",
        lessons: "الدروس النظرية",
        trafficSigns: "إشارات المرور",
        tests: "الاختبارات",
        videos: "دروس الفيديو",
        assistant: "المساعد الذكي",
        support: "الدعم",
        pricing: "الأسعار",
        login: "تسجيل الدخول",
        register: "إنشاء حساب",
        logout: "تسجيل الخروج",
        dashboard: "لوحة التحكم"
      },
      hero: {
        title: "اجتز اختبار النظري بلغتك!",
        subtitle: "المنصة الوحيدة بـ 9 لغات لرخصة القيادة السويدية B. اختبارات تدريبية تساعدك على الاستعداد لاختبار المعرفة النظري، دروس تفاعلية، ومساعد ذكي متاح على مدار الساعة.",
        confidence: "رخصة B النظرية",
        startTrial: "احصل على أسئلة مجانية",
        learnMore: "اعرف المزيد",
        trustedPlatform: "منصة تعليمية موثوقة",
        bLicense: "رخصة B - سيارات",
        languageBadge: "متوفر بـ 9 لغات",
        studentsCount: "انضم لأكثر من 1000 طالب"
      },
      howItWorks: {
        badge: "بسيط وفعّال",
        title: "3 خطوات لاجتياز اختبار النظري",
        step1Title: "سجّل وابدأ مجاناً",
        step1Desc: "أنشئ حسابك وجرّبأسئلة مجاناً — بدون دفع.",
        step2Title: "تدرّب بلغتك",
        step2Desc: "ادرس الدروس النظرية وقم بالاختبارات التدريبية باللغة التي تفهمها.",
        step3Title: "اجتز الاختبار الحقيقي",
        step3Desc: "أسئلتنا مصممة لمساعدتك على الاستعداد للامتحان النظري، ومتوفرة بـ 9 لغات لراحتك."
      },
      features: {
        title: "كل ما تحتاجه للنجاح",
        whyChooseUs: "لماذا تختارنا",
        explore: "استكشف",
        lessons: {
          title: "الدروس النظرية",
          desc: "دروس شاملة تغطي جميع قواعد المرور والإشارات والسلامة."
        },
        tests: {
          title: "اختبارات تدريبية",
          desc: "مئات الأسئلة لتحضيرك للاختبار الحقيقي."
        },
        videos: {
          title: "دروس فيديو",
          desc: "تعلم بصري مع مدربي قيادة محترفين."
        },
        ai: {
          title: "المساعد الذكي",
          desc: "احصل على إجابات فورية لأسئلتك على مدار الساعة."
        }
      },
      pricing: {
        title: "أسعار بسيطة وشفافة",
        subtitle: "ابدأ بأسئلة مجانية. للحصول على وصول غير محدود، اشترك الآن.",
        monthly: "شهري",
        sixMonths: "6 أشهر",
        perMonth: "kr/شهر",
        perYear: "kr",
        save: "وفر 48%",
        features: [
          "جميع الدروس النظرية",
          "اختبارات تدريبية غير محدودة",
          "دروس فيديو",
          "المساعد الذكي",
          "دعم متعدد اللغات"
        ],
        subscribe: "ابدأ الاشتراك",
        trial: "أسئلة مجانية، ثم",
        popular: "الأشهر",
        free: "مجاني",
        startFree: "ابدأ مجاناً",
        yourCurrentPlan: "خطتك الحالية"
      },
      cta: {
        startJourney: "ابدأ رحلتك نحو النجاح اليوم",
        joinStudents: "انضم إلى آلاف الطلاب الذين نجحوا في اختبارات القيادة بمساعدتنا"
      },
      auth: {
        login: "تسجيل الدخول",
        register: "إنشاء حساب",
        email: "البريد الإلكتروني",
        password: "كلمة المرور",
        name: "الاسم الكامل",
        confirmPassword: "تأكيد كلمة المرور",
        forgotPassword: "نسيت كلمة المرور؟",
        noAccount: "ليس لديك حساب؟",
        hasAccount: "لديك حساب بالفعل؟",
        loginBtn: "تسجيل الدخول",
        registerBtn: "إنشاء حساب",
        or: "أو",
        googleSignIn: "الدخول بـ Google",
        googleSignUp: "التسجيل بـ Google",
        freeTrialQuestions: "ابدأ مجاناً بأسئلة تدريبية",
        passwordMismatch: "كلمات المرور غير متطابقة",
        passwordTooShort: "كلمة المرور يجب أن تكون 6 أحرف على الأقل",
        pleaseWait: "يرجى الانتظار"
      },
      dashboard: {
        welcome: "مرحباً بعودتك",
        subscription: "الاشتراك",
        trial: "تجربة مجانية",
        active: "نشط",
        expired: "منتهي",
        daysLeft: "أيام متبقية",
        upgrade: "ترقية الآن",
        continueLearning: "تابع التعلم",
        quickActions: "إجراءات سريعة"
      },
      // Dashboard flat keys (underscore format) - Arabic
      dashboard_welcome: "مرحباً بعودتك",
      dashboard_subscription: "الاشتراك",
      dashboard_trial: "تجربة مجانية",
      dashboard_active: "نشط",
      dashboard_expired: "منتهي",
      dashboard_days_left: "أيام متبقية",
      dashboard_upgrade: "ترقية الآن",
      dashboard_continue_learning: "تابع التعلم",
      dashboard_quick_actions: "إجراءات سريعة",
      dashboard_lessons_title: "الدروس النظرية",
      dashboard_lessons_desc: "تعلم قواعد المرور والإشارات",
      dashboard_tests_title: "الاختبارات التدريبية",
      dashboard_tests_desc: "اختبر معرفتك",
      dashboard_videos_title: "دروس الفيديو",
      dashboard_videos_desc: "شاهد وتعلم",
      dashboard_assistant_title: "المساعد الذكي",
      dashboard_assistant_desc: "اسأل أي سؤال",
      dashboard_super_admin: "مدير عام",
      dashboard_admin: "مدير",
      dashboard_start_now: "ابدأ الآن",
      dashboard_read_more: "اقرأ المزيد",
      dashboard_subtitle: "إليك تقدمك في التعلم",
      // Lessons flat keys - Arabic
      lessons_title: "الدروس النظرية",
      lessons_subtitle: "تعلم قواعد المرور والسلامة على الطريق",
      lessons_no_lessons: "لا توجد دروس متاحة حالياً",
      lessons_loading: "جاري تحميل الدروس...",
      lessons_completed: "مكتمل",
      lessons_free: "مجاني",
      lessons_premium: "مميز",
      lessons_locked: "مقفل",
      lessons_minutes: "دقيقة",
      lessons_read_lesson: "اقرأ الدرس",
      // Tests flat keys - Arabic
      tests_questions: "سؤال",
      tests_minutes: "دقيقة",
      // Videos flat keys - Arabic
      videos_all_categories: "جميع الفئات",
      videos_count: "فيديو",
      videos_free: "مجاني",
      videos_locked: "مقفل",
      videos_loading: "جاري تحميل الفيديوهات...",
      videos_minutes: "دقيقة",
      videos_title: "دروس الفيديو",
      videos_subtitle: "شاهد وتعلم نظرية القيادة",
      videos_search: "البحث في الفيديوهات...",
      videos_new: "جديد",
      videos_no_results: "لم يتم العثور على فيديوهات",
      videos_trending: "رائج",
      videos_views: "مشاهدة",
      videos_watch: "مشاهدة",
      videos_watched: "تمت المشاهدة",
      // Support flat keys - Arabic
      support_title: "الدعم",
      support_subtitle: "نحن هنا للمساعدة",
      support_form_title: "أرسل لنا رسالة",
      support_form_subtitle: "سنرد خلال 24 ساعة",
      support_name: "اسمك",
      support_email: "بريدك الإلكتروني",
      support_subject: "الموضوع",
      support_message: "الرسالة",
      support_send: "إرسال الرسالة",
      support_sending: "جاري الإرسال...",
      support_thank_you: "شكراً لك!",
      support_success_msg: "تم إرسال رسالتك بنجاح",
      support_send_another: "إرسال رسالة أخرى",
      support_faq_title: "الأسئلة الشائعة",
      lessons: {
        title: "الدروس النظرية",
        categories: {
          traffic_signs: "إشارات المرور",
          road_rules: "قواعد الطريق",
          safety: "السلامة",
          parking: "الوقوف"
        },
        readMore: "اقرأ المزيد"
      },
      lessons_category_traffic_rules: "قواعد المرور",
      lessons_category_traffic_signs: "إشارات المرور",
      lessons_category_road_rules: "قواعد الطريق",
      lessons_category_safety: "السلامة",
      lessons_category_parking: "الوقوف",
      lessons_category_general: "عام",
      lessons_category_theory: "نظري",
      tests: {
        title: "الاختبارات التدريبية",
        startTest: "ابدأ الاختبار",
        question: "سؤال",
        of: "من",
        submit: "إرسال الاختبار",
        next: "التالي",
        previous: "السابق",
        results: "نتائج الاختبار",
        score: "نتيجتك",
        correct: "إجابات صحيحة",
        incorrect: "خاطئة",
        tryAgain: "حاول مرة أخرى",
        passed: "تهانينا! لقد نجحت!",
        failed: "واصل التدريب. يمكنك النجاح!"
      },
      videos: {
        title: "دروس الفيديو",
        duration: "المدة",
        watch: "شاهد الآن"
      },
      subscription: {
        neverExpires: "اشتراك مدى الحياة - لا ينتهي أبداً ✨",
        active: "نشط",
        expired: "منتهي",
        trial: "تجريبي",
        lifetime: "مدى الحياة"
      },
      settings: {
        expires: "ينتهي في",
        trialEnds: "التجربة تنتهي في",
        title: "الإعدادات",
        account: "الحساب",
        language: "اللغة"
      },
      assistant: {
        title: "مساعد القيادة الذكي",
        placeholder: "اسألني أي شيء عن القيادة...",
        send: "إرسال",
        thinking: "جارٍ التفكير..."
      },
      mistakes: {
        commonMistakes: "الأخطاء الشائعة"
      },
      support: {
        title: "الدعم",
        subtitle: "نحن هنا للمساعدة! أرسل لنا رسالة وسنتواصل معك في أقرب وقت.",
        form: {
          title: "أرسل لنا رسالة",
          subtitle: "سنرد خلال 24 ساعة"
        },
        contact: {
          email: { en: "Email", ar: "البريد الإلكتروني", sv: "E-post" },
          phone: { en: "Phone", ar: "الهاتف", sv: "Telefon" },
          hours: { en: "Support Hours", ar: "ساعات الدعم", sv: "Supporttider" }
        },
        faq: {
          title: "الأسئلة الشائعة",
          questions: {
            howToStart: {
              question: "كيف أبدأ باستخدام المنصة؟",
              answer: "قم بالتسجيل للحصول على حساب وستحصل على أسئلة مجانية لتجربة منصتنا."
            },
            subscriptionPlans: {
              question: "ما هي خطط الاشتراك المتاحة؟",
              answer: "نوفر 3 خطط: شهر واحد، 3 أشهر و6 أشهر، مع وصول غير محدود لجميع المحتوى. الدفع لمرة واحدة ولا يتجدد تلقائياً."
            },
            languageChange: {
              question: "كيف يمكنني تغيير اللغة؟",
              answer: "استخدم محدد اللغة في القائمة العلوية للتبديل بين اللغات."
            }
          }
        }
      },
      footer: {
        rights: "جميع الحقوق محفوظة",
        privacy: "سياسة الخصوصية",
        terms: "شروط الخدمة"
      },
      common: {
        loading: "جارٍ التحميل...",
        error: "حدث خطأ",
        success: "تم بنجاح",
        cancel: "إلغاء",
        save: "حفظ",
        back: "رجوع",
        delete: "حذف",
        edit: "تعديل",
        add: "إضافة",
        search: "بحث",
        refresh: "تحديث",
        actions: "إجراءات",
        filter: "تصفية",
        next: "التالي",
        previous: "السابق",
        submit: "إرسال",
        close: "إغلاق",
        confirm: "تأكيد",
        redirecting: "جاري التوجيه...",
        returningToLogin: "جاري العودة لصفحة تسجيل الدخول..."
      },
      admin: {
        dataRefreshed: "تم تحديث البيانات بنجاح",
        welcome: "مرحباً بعودتك، المشرف!",
        welcomeSubtitle: "إليك ما يحدث في مدرسة القيادة اليوم.",
        menu: {
          dashboard: "لوحة التحكم",
          users: "المستخدمون",
          lessons: "الدروس",
          questions: "الأسئلة",
          videos: "الفيديوهات",
          payments: "المدفوعات",
          settings: "الإعدادات",
          backup: "النسخ الاحتياطي"
        },
        stats: {
          totalUsers: "إجمالي المستخدمين",
          activeSubscribers: "المشتركون النشطون",
          monthlyRevenue: "الإيرادات الشهرية",
          contentItems: "عناصر المحتوى",
          users: "المستخدمين",
          active: "نشط",
          trial: "تجريبي",
          lessons: "الدروس",
          questions: "الأسئلة",
          videos: "الفيديوهات",
          payments: "المدفوعات"
        },
        title: "لوحة تحكم المسؤول",
        subtitle: "إدارة منصة تعليم القيادة",
        tabs: {
          users: "المستخدمين",
          lessons: "الدروس",
          questions: "الأسئلة",
          videos: "الفيديوهات",
          payments: "المدفوعات"
        },
        users: {
          searchPlaceholder: "بحث عن مستخدمين...",
          name: "الاسم",
          email: "البريد",
          status: "الحالة",
          plan: "الخطة",
          editSubscription: "تعديل الاشتراك",
          daysToAdd: "أيام للإضافة"
        },
        content: {
          addLesson: "إضافة درس",
          addQuestion: "إضافة سؤال",
          addVideo: "إضافة فيديو",
          titleEn: "العنوان (EN)",
          titleAr: "العنوان (AR)",
          titleSv: "العنوان (SV)",
          contentEn: "المحتوى (EN)",
          contentAr: "المحتوى (AR)",
          contentSv: "المحتوى (SV)",
          category: "الفئة",
          imageUrl: "رابط الصورة",
          order: "الترتيب",
          options: "الخيارات",
          correctAnswer: "الإجابة الصحيحة",
          explanation: "التفسير",
          videoUrl: "رابط الفيديو",
          thumbnailUrl: "صورة مصغرة",
          duration: "المدة (ثواني)",
          totalItems: "عنصر إجمالي",
          noLessons: "لا توجد دروس بعد. أضف أول درس!",
          noQuestions: "لا توجد أسئلة بعد. أضف أول سؤال!",
          noVideos: "لا توجد فيديوهات بعد. أضف أول فيديو!",
          noPayments: "لم يتم تسجيل أي دفعات بعد."
        },
        payments: {
          date: "التاريخ",
          amount: "المبلغ"
        },
        export: {
          csv: "تصدير CSV",
          success: "تم تصدير البيانات بنجاح!"
        },
        filters: {
          all: "الكل",
          clear: "مسح الفلاتر",
          results: "نتيجة"
        },
        delete: {
          confirm: "هل أنت متأكد؟ لا يمكن التراجع عن هذا الإجراء.",
          confirmUser: "هل أنت متأكد من حذف {{name}}؟ لا يمكن التراجع عن هذا الإجراء.",
          userSuccess: "تم حذف المستخدم بنجاح",
          userError: "فشل حذف المستخدم",
          success: "تم الحذف بنجاح",
          error: "فشل الحذف"
        },
        ban: {
          title: "حظر المستخدم",
          confirm: "هل أنت متأكد من حظر {{name}}؟",
          success: "تم حظر المستخدم بنجاح",
          error: "فشل حظر المستخدم"
        },
        unban: {
          title: "إلغاء الحظر",
          success: "تم إلغاء الحظر بنجاح",
          error: "فشل إلغاء الحظر"
        },
        status: {
          banned: "محظور"
        },
        subscription: {
          updated: "تم تحديث الاشتراك بنجاح",
          error: "فشل تحديث الاشتراك"
        },
        save: {
          error: "فشل حفظ التغييرات"
        },
        create: {
          success: "تم الإنشاء بنجاح",
          lesson: "إنشاء درس جديد",
          question: "إنشاء سؤال جديد",
          video: "إنشاء فيديو جديد"
        },
        update: {
          success: "تم التحديث بنجاح"
        },
        edit: {
          lesson: "تعديل الدرس",
          question: "تعديل السؤال",
          video: "تعديل الفيديو"
        },
        manage: {
          users: "إدارة جميع المستخدمين المسجلين والاشتراكات والصلاحيات"
        }
      },
      resetPassword: {
        title: "إعادة تعيين كلمة المرور",
        subtitle: "أدخل بريدك الإلكتروني لتلقي رابط إعادة التعيين",
        sendLink: "إرسال رابط إعادة التعيين",
        backToLogin: "العودة لتسجيل الدخول",
        emailSent: "تم إرسال رابط إعادة التعيين إلى بريدك",
        newPassword: "كلمة المرور الجديدة",
        confirmNewPassword: "تأكيد كلمة المرور الجديدة",
        resetSuccess: "تم إعادة تعيين كلمة المرور بنجاح"
      },
      ui: {
        overview: "نظرة عامة",
        theoryLessons: "الدروس النظرية",
        trafficSigns: "إشارات المرور",
        practiceTests: "اختبارات تدريبية",
        quickTips: "نصائح سريعة",
        importantWords: "كلمات هامة",
        howToUse: "طريقة الاستخدام",
        reviews: "التقييمات",
        aiAssistant: "المساعد الذكي",
        prices: "الأسعار",
        settings: "الإعدادات",
        logout: "تسجيل الخروج",
        login: "دخول",
        register: "تسجيل",
        subscribersOnly: "محتوى للمشتركين فقط",
        subscribeNow: "اشترك الآن",
        backToFreeTests: "العودة للاختبارات المجانية",
        unlimitedQuestions: "أسئلة غير محدودة",
        allTheoryLessons: "جميع الدروس النظرية",
        tutorialVideos: "مساعد ذكي AI",
        subscribeDesc: "اشترك الآن للوصول إلى جميع الدروس، إشارات المرور، والمزيد!",
        currentPlan: "خطتك الحالية",
        sixMonths: "6 أشهر",
        perMonth: "/شهر",
        profile: "الملف الشخصي",
        subscribed: "مشترك",
        trial: "تجريبي",
        subscribe: "اشترك",
        free: "مجاني",
        startTest: "ابدأ الاختبار",
        forSubscribers: "للمشتركين",
        subscribeForAccess: "اشترك للوصول الكامل!",
        later: "لاحقاً",
        learnTrafficSigns: "تعلم إشارات المرور",
        goldenTips: "نصائح ذهبية للاختبار",
        demoAccount: "حساب تجريبي",
        demoFullAccess: "حساب تجريبي - وصول كامل",
        timeRemaining: "الوقت المتبقي للحساب",
        accountExpired: "انتهت صلاحية الحساب",
        enjoyFeatures: "استمتع بجميع الميزات!",
        upgradeNow: "ترقية الآن",
        renewNow: "جدد الآن",
        renewSubscription: "تجديد الاشتراك الآن",
        requestRefund: "طلب استرداد المبلغ",
        refundUnderReview: "طلب الاسترداد قيد المراجعة",
        refundContactSoon: "سيتم التواصل معك قريباً بخصوص طلبك.",
        refundReason: "سبب طلب الاسترداد",
        explainRefund: "الرجاء توضيح سبب طلب الاسترداد...",
        submitRequest: "إرسال الطلب",
        submitting: "جاري الإرسال...",
        refundAlreadyRequested: "تم طلب الاسترداد مسبقاً",
        noActiveSubscription: "لا يوجد اشتراك نشط للاسترداد",
        refundRequestSuccess: "تم إرسال طلب الاسترداد بنجاح"
      },
      footer: {
        privacy: "الخصوصية",
        terms: "الشروط",
        refund: "الاسترجاع",
        disclaimer: "إخلاء المسؤولية",
        cookies: "الكوكيز"
      }
    }
  },
  sv: {
    translation: {
      nav: {
        home: "Hem",
        lessons: "Teorilektioner",
        trafficSigns: "Trafikmärken",
        tests: "Övningsprov",
        videos: "Videolektioner",
        assistant: "AI-Assistent",
        support: "Stöd",
        pricing: "Priser",
        login: "Logga in",
        register: "Registrera",
        logout: "Logga ut",
        dashboard: "Översikt"
      },
      hero: {
        title: "Klara körkortsteorin på ditt språk",
        subtitle: "Enda plattformen med 9 språk för svenskt B-körkort. Övningsprov som hjälper dig förbereda dig för kunskapsprovet, interaktiva lektioner och AI-assistent dygnet runt.",
        confidence: "B-körkort Teori",
        startTrial: "Få gratis frågor",
        learnMore: "Läs mer",
        trustedPlatform: "Pålitlig lärplattform",
        bLicense: "B-körkort",
        languageBadge: "Tillgänglig på 9 språk",
        studentsCount: "Gå med 800+ elever"
      },
      howItWorks: {
        badge: "Enkelt & effektivt",
        title: "3 steg för att klara teoriprovet",
        step1Title: "Registrera dig & börja gratis",
        step1Desc: "Skapa konto och testa frågor gratis — ingen betalning krävs.",
        step2Title: "Öva på ditt språk",
        step2Desc: "Studera teorilektioner och gör övningsprov på det språk du förstår bäst.",
        step3Title: "Klara kunskapsprovet",
        step3Desc: "Våra frågor hjälper dig att förbereda dig för kunskapsprovet och finns på 9 språk för din bekvämlighet."
      },
      features: {
        title: "Allt du behöver för att klara provet",
        whyChooseUs: "Varför välja oss",
        explore: "Utforska",
        lessons: {
          title: "Teorilektioner",
          desc: "Omfattande lektioner som täcker alla trafikregler, skyltar och säkerhet."
        },
        tests: {
          title: "Övningsprov",
          desc: "Hundratals frågor för att förbereda dig för kunskapsprovet."
        },
        videos: {
          title: "Videolektioner",
          desc: "Visuell inlärning med professionella körlärare."
        },
        ai: {
          title: "AI-Assistent",
          desc: "Få omedelbara svar på dina körfrågor dygnet runt."
        }
      },
      pricing: {
        title: "Enkla, Transparenta Priser",
        subtitle: "Börja med gratis frågor. Prenumerera för obegränsad åtkomst.",
        monthly: "Månadsvis",
        sixMonths: "6 månader",
        perMonth: "kr/månad",
        perYear: "kr",
        save: "Spara 48%",
        features: [
          "Alla teorilektioner",
          "Obegränsade övningsprov",
          "Videolektioner",
          "AI-assistent",
          "Flerspråkigt stöd"
        ],
        subscribe: "Starta prenumeration",
        trial: "Gratis frågor, sedan",
        popular: "Populär",
        free: "Gratis",
        startFree: "Starta gratis",
        yourCurrentPlan: "Din nuvarande plan"
      },
      cta: {
        startJourney: "Börja din resa mot framgång idag",
        joinStudents: "Gå med tusentals studenter som har klarat sina körprov med vår hjälp"
      },
      auth: {
        login: "Logga in",
        register: "Skapa konto",
        email: "E-post",
        password: "Lösenord",
        name: "Fullständigt namn",
        confirmPassword: "Bekräfta lösenord",
        forgotPassword: "Glömt lösenord?",
        noAccount: "Har du inget konto?",
        hasAccount: "Har du redan ett konto?",
        loginBtn: "Logga in",
        registerBtn: "Skapa konto",
        or: "eller",
        googleSignIn: "Logga in med Google",
        googleSignUp: "Registrera med Google",
        freeTrialQuestions: "Kom igång gratis med provfrågor",
        passwordMismatch: "Lösenorden matchar inte",
        passwordTooShort: "Lösenordet måste vara minst 6 tecken",
        pleaseWait: "Vänligen vänta"
      },
      dashboard: {
        welcome: "Välkommen tillbaka",
        subscription: "Prenumeration",
        trial: "Gratis provperiod",
        active: "Aktiv",
        expired: "Utgången",
        daysLeft: "dagar kvar",
        upgrade: "Uppgradera nu",
        continueLearning: "Fortsätt lära",
        quickActions: "Snabbåtgärder"
      },
      // Dashboard flat keys (underscore format) - Swedish
      dashboard_welcome: "Välkommen tillbaka",
      dashboard_subscription: "Prenumeration",
      dashboard_trial: "Gratis provperiod",
      dashboard_active: "Aktiv",
      dashboard_expired: "Utgången",
      dashboard_days_left: "dagar kvar",
      dashboard_upgrade: "Uppgradera nu",
      dashboard_continue_learning: "Fortsätt lära",
      dashboard_quick_actions: "Snabbåtgärder",
      dashboard_lessons_title: "Teorilektioner",
      dashboard_lessons_desc: "Lär dig trafikregler och skyltar",
      dashboard_tests_title: "Övningsprov",
      dashboard_tests_desc: "Testa dina kunskaper",
      dashboard_videos_title: "Videolektioner",
      dashboard_videos_desc: "Titta och lär",
      dashboard_assistant_title: "AI-Assistent",
      dashboard_assistant_desc: "Ställ valfri fråga",
      dashboard_super_admin: "Superadmin",
      dashboard_admin: "Admin",
      dashboard_start_now: "Börja nu",
      dashboard_read_more: "Läs mer",
      dashboard_subtitle: "Här är din inlärningsframsteg",
      // Lessons flat keys - Swedish
      lessons_title: "Teorilektioner",
      lessons_subtitle: "Lär dig trafikregler och vägsäkerhet",
      lessons_no_lessons: "Inga lektioner tillgängliga än",
      lessons_loading: "Laddar lektioner...",
      lessons_completed: "Slutförd",
      lessons_free: "Gratis",
      lessons_premium: "Premium",
      lessons_locked: "Låst",
      lessons_minutes: "minuter",
      lessons_read_lesson: "Läs lektion",
      // Tests flat keys - Swedish
      tests_questions: "frågor",
      tests_minutes: "minuter",
      // Videos flat keys - Swedish
      videos_all_categories: "Alla Kategorier",
      videos_count: "videor",
      videos_free: "Gratis",
      videos_locked: "Låst",
      videos_loading: "Laddar videor...",
      videos_minutes: "minuter",
      videos_title: "Videolektioner",
      videos_subtitle: "Titta och lär dig körteori",
      videos_search: "Sök videor...",
      videos_new: "Ny",
      videos_no_results: "Inga videor hittades",
      videos_trending: "Populär",
      videos_views: "visningar",
      videos_watch: "Titta",
      videos_watched: "Tittad",
      // Support flat keys - Swedish
      support_title: "Support",
      support_subtitle: "Vi finns här för att hjälpa",
      support_form_title: "Skicka oss ett meddelande",
      support_form_subtitle: "Vi svarar inom 24 timmar",
      support_name: "Ditt namn",
      support_email: "Din E-post",
      support_subject: "Ämne",
      support_message: "Meddelande",
      support_send: "Skicka meddelande",
      support_sending: "Skickar...",
      support_thank_you: "Tack!",
      support_success_msg: "Ditt meddelande har skickats",
      support_send_another: "Skicka ett till meddelande",
      support_faq_title: "Vanliga frågor",
      lessons: {
        title: "Teorilektioner",
        categories: {
          traffic_signs: "Trafikskyltar",
          road_rules: "Trafikregler",
          safety: "Säkerhet",
          parking: "Parkering"
        },
        readMore: "Läs mer"
      },
      lessons_category_traffic_rules: "Trafikregler",
      lessons_category_traffic_signs: "Trafikskyltar",
      lessons_category_road_rules: "Vägregler",
      lessons_category_safety: "Säkerhet",
      lessons_category_parking: "Parkering",
      lessons_category_general: "Allmänt",
      lessons_category_theory: "Teori",
      tests: {
        title: "Övningsprov",
        startTest: "Starta prov",
        question: "Fråga",
        of: "av",
        submit: "Skicka prov",
        next: "Nästa",
        previous: "Föregående",
        results: "Provresultat",
        score: "Ditt resultat",
        correct: "Rätta svar",
        incorrect: "Felaktiga",
        tryAgain: "Försök igen",
        passed: "Grattis! Du klarade det!",
        failed: "Fortsätt öva. Du kan klara det!"
      },
      videos: {
        title: "Videolektioner",
        duration: "Längd",
        watch: "Titta nu"
      },
      subscription: {
        neverExpires: "Livstidsprenumeration - går aldrig ut ✨",
        active: "Aktiv",
        expired: "Utgången",
        trial: "Provperiod",
        lifetime: "Livstid"
      },
      settings: {
        expires: "Går ut",
        trialEnds: "Provperioden slutar",
        title: "Inställningar",
        account: "Konto",
        language: "Språk"
      },
      assistant: {
        title: "AI Körassistent",
        placeholder: "Fråga mig vad som helst om körning...",
        send: "Skicka",
        thinking: "Tänker..."
      },
      mistakes: {
        commonMistakes: "Vanliga Misstag"
      },
      support: {
        title: "Support",
        subtitle: "Vi är här för att hjälpa! Skicka oss ett meddelande så återkommer vi så snart som möjligt.",
        form: {
          title: "Skicka oss ett meddelande",
          subtitle: "Vi svarar inom 24 timmar"
        },
        contact: {
          email: { en: "Email", ar: "البريد الإلكتروني", sv: "E-post" },
          phone: { en: "Phone", ar: "الهاتف", sv: "Telefon" },
          hours: { en: "Support Hours", ar: "ساعات الدعم", sv: "Supporttider" }
        },
        faq: {
          title: "Vanliga frågor",
          questions: {
            howToStart: {
              question: "Hur börjar jag använda plattformen?",
              answer: "Registrera dig för ett konto så får du gratis frågor för att prova vår plattform."
            },
            subscriptionPlans: {
              question: "Vilka prenumerationsplaner finns tillgängliga?",
              answer: "Vi erbjuder tre planer: 1 månad, 3 månader och 6 månader med obegränsad tillgång till allt innehåll. Engångsbetalning – förnyas inte automatiskt."
            },
            languageChange: {
              question: "Hur kan jag ändra språket?",
              answer: "Använd språkväljaren i den övre navigeringsmenyn för att växla mellan språk."
            }
          }
        }
      },
      footer: {
        rights: "Alla rättigheter förbehållna",
        privacy: "Integritetspolicy",
        terms: "Användarvillkor"
      },
      common: {
        loading: "Laddar...",
        error: "Ett fel uppstod",
        success: "Lyckades",
        cancel: "Avbryt",
        save: "Spara",
        back: "Tillbaka",
        delete: "Radera",
        edit: "Redigera",
        add: "Lägg till",
        search: "Sök",
        refresh: "Uppdatera",
        actions: "Åtgärder",
        filter: "Filtrera",
        next: "Nästa",
        previous: "Föregående",
        submit: "Skicka",
        close: "Stäng",
        confirm: "Bekräfta",
        redirecting: "Omdirigerar...",
        returningToLogin: "Återgår till inloggning..."
      },
      admin: {
        dataRefreshed: "Data uppdaterad framgångsrikt",
        welcome: "Välkommen tillbaka, Admin!",
        welcomeSubtitle: "Här är vad som händer med din körskola idag.",
        menu: {
          dashboard: "Instrumentpanel",
          users: "Användare",
          lessons: "Lektioner",
          questions: "Frågor",
          videos: "Videor",
          payments: "Betalningar",
          settings: "Inställningar",
          backup: "Backup"
        },
        stats: {
          totalUsers: "Totalt användare",
          activeSubscribers: "Aktiva prenumeranter",
          monthlyRevenue: "Månadsinkomst",
          contentItems: "Innehållsobjekt",
          users: "Användare",
          active: "Aktiva",
          trial: "Prov",
          lessons: "Lektioner",
          questions: "Frågor",
          videos: "Videor",
          payments: "Betalningar"
        },
        title: "Adminpanel",
        subtitle: "Hantera din trafikskola",
        tabs: {
          users: "Användare",
          lessons: "Lektioner",
          questions: "Frågor",
          videos: "Videor",
          payments: "Betalningar"
        },
        users: {
          searchPlaceholder: "Sök användare...",
          name: "Namn",
          email: "E-post",
          status: "Status",
          plan: "Plan",
          editSubscription: "Redigera prenumeration",
          daysToAdd: "Dagar att Lägga till"
        },
        content: {
          addLesson: "Lägg till Lektion",
          addQuestion: "Lägg till Fråga",
          addVideo: "Lägg till Video",
          titleEn: "Titel (EN)",
          titleAr: "Titel (AR)",
          titleSv: "Titel (SV)",
          contentEn: "Innehåll (EN)",
          contentAr: "Innehåll (AR)",
          contentSv: "Innehåll (SV)",
          category: "Kategori",
          imageUrl: "Bild URL",
          order: "Ordning",
          options: "Alternativ",
          correctAnswer: "Rätt svar",
          explanation: "Förklaring",
          videoUrl: "Video URL",
          thumbnailUrl: "Miniatyrbild URL",
          duration: "Längd (sekunder)",
          totalItems: "totalt objekt",
          noLessons: "Inga lektioner än. Lägg till din första lektion!",
          noQuestions: "Inga frågor än. Lägg till din första fråga!",
          noVideos: "Inga videor än. Lägg till din första video!",
          noPayments: "Inga betalningar registrerade än."
        },
        payments: {
          date: "Datum",
          amount: "Belopp"
        },
        export: {
          csv: "Exportera CSV",
          success: "Data exporterad framgångsrikt!"
        },
        filters: {
          all: "Alla",
          clear: "Rensa Filter",
          results: "resultat"
        },
        delete: {
          confirm: "Är du säker? Denna åtgärd kan inte ångras.",
          confirmUser: "Är du säker på att du vill radera {{name}}? Denna åtgärd kan inte ångras.",
          userSuccess: "Användare raderad",
          userError: "Misslyckades radera användare",
          success: "Raderad framgångsrikt",
          error: "Misslyckades radera"
        },
        ban: {
          title: "Blockera Användare",
          confirm: "Är du säker på att du vill blockera {{name}}?",
          success: "Användare blockerad",
          error: "Misslyckades blockera användare"
        },
        unban: {
          title: "Avblockera Användare",
          success: "Användare avblockerad",
          error: "Misslyckades avblockera användare"
        },
        status: {
          banned: "Blockerad"
        },
        subscription: {
          updated: "Prenumeration uppdaterad",
          error: "Misslyckades uppdatera prenumeration"
        },
        save: {
          error: "Misslyckades spara ändringar"
        },
        create: {
          success: "Skapad framgångsrikt",
          lesson: "Skapa Ny Lektion",
          question: "Skapa Ny Fråga",
          video: "Skapa Ny Video"
        },
        update: {
          success: "Uppdaterad framgångsrikt"
        },
        edit: {
          lesson: "Redigera Lektion",
          question: "Redigera Fråga",
          video: "Redigera Video"
        },
        manage: {
          users: "Hantera alla registrerade användare, prenumerationer och behörigheter"
        }
      },
      resetPassword: {
        title: "Återställ Lösenord",
        subtitle: "Ange din e-post för att få återställningslänk",
        sendLink: "Skicka Återställningslänk",
        backToLogin: "Tillbaka till Inloggning",
        emailSent: "Återställningslänk skickad till din e-post",
        newPassword: "Nytt Lösenord",
        confirmNewPassword: "Bekräfta Nytt Lösenord",
        resetSuccess: "Lösenordet har återställts"
      },
      ui: {
        overview: "Översikt",
        theoryLessons: "Teorilektioner",
        trafficSigns: "Trafikmärken",
        practiceTests: "Övningsprov",
        quickTips: "Snabba Råd",
        importantWords: "Viktiga Ord",
        howToUse: "Hur man använder",
        reviews: "Recensioner",
        aiAssistant: "AI-Assistent",
        prices: "Priser",
        settings: "Inställningar",
        logout: "Logga ut",
        login: "Logga in",
        register: "Registrera",
        subscribersOnly: "Endast för prenumeranter",
        subscribeNow: "Prenumerera nu",
        backToFreeTests: "Tillbaka till gratis tester",
        unlimitedQuestions: "Obegränsade frågor",
        allTheoryLessons: "Alla teorilektioner",
        tutorialVideos: "AI-assistent",
        subscribeDesc: "Prenumerera nu för att få tillgång till alla lektioner, trafikskyltar och mer!",
        currentPlan: "Din nuvarande plan",
        sixMonths: "6 Månader",
        perMonth: "/månad",
        profile: "Profil",
        subscribed: "Prenumerant",
        trial: "Provperiod",
        subscribe: "Prenumerera",
        free: "Gratis",
        startTest: "Starta test",
        forSubscribers: "För prenumeranter",
        subscribeForAccess: "Prenumerera för full åtkomst!",
        later: "Senare",
        learnTrafficSigns: "Lär dig trafikmärken",
        goldenTips: "Gyllene tips för provet",
        demoAccount: "Demokonto",
        demoFullAccess: "Demokonto - Full tillgång",
        timeRemaining: "Återstående tid",
        accountExpired: "Kontot har utgått",
        enjoyFeatures: "Njut av alla funktioner!",
        upgradeNow: "Uppgradera nu",
        renewNow: "Förnya nu",
        renewSubscription: "Förnya prenumeration nu",
        requestRefund: "Begär återbetalning",
        refundUnderReview: "Återbetalningsbegäran under granskning",
        refundContactSoon: "Vi kommer att kontakta dig snart angående din begäran.",
        refundReason: "Anledning till återbetalning",
        explainRefund: "Förklara varför du begär återbetalning...",
        submitRequest: "Skicka begäran",
        submitting: "Skickar...",
        refundAlreadyRequested: "Återbetalning har redan begärts",
        noActiveSubscription: "Ingen aktiv prenumeration att återbetala",
        refundRequestSuccess: "Din återbetalningsbegäran har skickats"
      },
      footer: {
        privacy: "Integritet",
        terms: "Villkor",
        refund: "Återbetalning",
        disclaimer: "Ansvar",
        cookies: "Cookies"
      }
    }
  },
  // Turkish - Türkçe
  tr: {
    translation: {
      nav: { home: "Ana Sayfa", lessons: "Teori Dersleri", trafficSigns: "Trafik İşaretleri", tests: "Pratik Testler", videos: "Video Dersler", assistant: "AI Asistan", support: "Destek", pricing: "Fiyatlar", login: "Giriş", register: "Kayıt Ol", logout: "Çıkış", dashboard: "Panel" },
      hero: { title: "Ehliyet Teorisini Geç Kendi Dilinde", subtitle: "İsveç B ehliyeti için 9 dilde tek platform. Kunskapsprov'a hazırlanmanıza yardımcı olan pratik sorular, etkileşimli dersler ve 7/24 AI asistan.", confidence: "B Ehliyet Teorisi", startTrial: "Ücretsiz Soruları Dene", learnMore: "Daha Fazla", trustedPlatform: "Güvenilir Öğrenme Platformu", bLicense: "B Ehliyeti", languageBadge: "9 dilde mevcut", studentsCount: "800+ öğrenciye katılın" },
      howItWorks: { badge: "Basit & Etkili", title: "Teoriyi Geçmek İçin 3 Adım", step1Title: "Kayıt Ol & Ücretsiz Başla", step1Desc: "Hesap oluştur ve soruları ücretsiz dene.", step2Title: "Kendi Dilinde Çalış", step2Desc: "En iyi anladığın dilde ders çalış ve test çöz.", step3Title: "Teori Sınavını Geç", step3Desc: "Sorularımız kunskapsprov'a hazırlanmanıza yardımcı olmak için tasarlanmış olup kolaylığınız için 9 dilde mevcuttur." },
      features: { title: "Başarı İçin İhtiyacınız Olan Her Şey", whyChooseUs: "Neden Bizi Seçmelisiniz", explore: "Keşfet", lessons: { title: "Teori Dersleri", desc: "Tüm trafik kuralları, işaretler ve güvenlik konularını kapsayan kapsamlı dersler." }, tests: { title: "Pratik Testler", desc: "Teori sınavına hazırlık için yüzlerce soru." }, videos: { title: "Video Eğitimler", desc: "Profesyonel sürüş eğitmenleri ile görsel öğrenme." }, ai: { title: "AI Asistan", desc: "Sürüş sorularınıza 7/24 anında cevap alın." } },
      pricing: { title: "Basit, Şeffaf Fiyatlandırma", subtitle: "Ücretsiz sorularla başlayın. Sınırsız erişim için abone olun.", monthly: "Aylık", sixMonths: "6 Ay", perMonth: "kr/ay", subscribe: "Aboneliği Başlat", free: "Ücretsiz", startFree: "Ücretsiz Başla", popular: "Popüler", yourCurrentPlan: "Mevcut planınız" },
      cta: { startJourney: "Başarıya Giden Yolculuğunuza Bugün Başlayın", joinStudents: "Yardımımızla sürüş sınavını geçen binlerce öğrenciye katılın" },
      auth: { login: "Giriş", register: "Hesap Oluştur", email: "E-posta", password: "Şifre", name: "Ad Soyad", confirmPassword: "Şifreyi Onayla", forgotPassword: "Şifremi unuttum?", noAccount: "Hesabınız yok mu?", hasAccount: "Zaten hesabınız var mı?", loginBtn: "Giriş Yap", registerBtn: "Hesap Oluştur", or: "veya", googleSignIn: "Google ile giriş yap", googleSignUp: "Google ile kaydol", freeTrialQuestions: "Ücretsiz deneme sorularıyla başla", passwordMismatch: "Şifreler eşleşmiyor", passwordTooShort: "Şifre en az 6 karakter olmalı", pleaseWait: "Lütfen bekleyin" },
      dashboard: { welcome: "Tekrar hoşgeldiniz", subscription: "Abonelik", trial: "Ücretsiz Deneme", active: "Aktif", expired: "Süresi Dolmuş", daysLeft: "gün kaldı", upgrade: "Yükselt" },
      tests: { title: "Pratik Testler", startTest: "Teste Başla", question: "Soru", of: "/", submit: "Testi Gönder", next: "Sonraki", previous: "Önceki", results: "Test Sonuçları", score: "Puanınız", correct: "Doğru Cevaplar", incorrect: "Yanlış", tryAgain: "Tekrar Dene", passed: "Tebrikler! Geçtiniz!", failed: "Pratik yapmaya devam edin!" },
      assistant: { title: "AI Sürüş Asistanı", placeholder: "Sürüş hakkında bir şey sorun...", send: "Gönder", thinking: "Düşünüyor..." },
      support: { title: "Destek", subtitle: "Size yardımcı olmak için buradayız", form_title: "Bize mesaj gönderin", name: "Adınız", email: "E-postanız", subject: "Konu", message: "Mesaj", send: "Gönder", thank_you: "Teşekkürler!" },
      support_title: "Destek",
      support_subtitle: "Size yardımcı olmak için buradayız",
      support_form_title: "Bize mesaj gönderin",
      support_form_subtitle: "24 saat içinde yanıt vereceğiz",
      support_name: "Adınız",
      support_email: "E-postanız",
      support_subject: "Konu",
      support_message: "Mesaj",
      support_send: "Mesaj Gönder",
      support_sending: "Gönderiliyor...",
      support_thank_you: "Teşekkürler!",
      support_success_msg: "Mesajınız başarıyla gönderildi",
      support_send_another: "Başka bir mesaj gönder",
      support_faq_title: "Sık Sorulan Sorular",
      lessons_title: "Teori Dersleri", lessons_subtitle: "Trafik kurallarını ve yol güvenliğini öğrenin", tests_questions: "soru", tests_minutes: "dakika", settings_title: "Ayarlar", settings_language: "Dil", settings_save: "Kaydet",
      back: "Geri", chooseTest: "Bir Test Seçin", profile: "Profil", edit: "Düzenle", save: "Kaydet", cancel: "İptal", loading: "Yükleniyor...", noData: "Veri yok",
      dashboard_welcome: "Tekrar hoşgeldiniz", dashboard_subscription: "Abonelik", dashboard_trial: "Ücretsiz Deneme", dashboard_active: "Aktif", dashboard_expired: "Süresi Dolmuş", dashboard_days_left: "gün kaldı", dashboard_upgrade: "Yükselt", dashboard_continue_learning: "Öğrenmeye Devam Et", dashboard_quick_actions: "Hızlı İşlemler", dashboard_lessons_title: "Teori Dersleri", dashboard_lessons_desc: "Trafik kurallarını öğrenin", dashboard_tests_title: "Pratik Testler", dashboard_tests_desc: "Bilginizi test edin", dashboard_videos_title: "Video Dersler", dashboard_videos_desc: "İzle ve öğren", dashboard_assistant_title: "AI Asistan", dashboard_assistant_desc: "Herhangi bir soru sorun", dashboard_subtitle: "İşte öğrenme ilerlemeniz",
      common: { loading: "Yükleniyor...", error: "Bir hata oluştu", success: "Başarılı", cancel: "İptal", save: "Kaydet", back: "Geri", delete: "Sil", edit: "Düzenle", add: "Ekle", search: "Ara", next: "Sonraki", previous: "Önceki", submit: "Gönder", close: "Kapat", confirm: "Onayla", redirecting: "Yönlendiriliyor...", returningToLogin: "Giriş sayfasına dönülüyor..." },
      subscription: { neverExpires: "Ömür boyu abonelik - asla bitmez ✨", active: "Aktif", expired: "Süresi Dolmuş", trial: "Deneme", lifetime: "Ömür Boyu" },
      settings: { expires: "Bitiş tarihi", trialEnds: "Deneme bitiş", title: "Ayarlar", account: "Hesap", language: "Dil" },
      ui: { overview: "Genel Bakış", theoryLessons: "Teori Dersleri", trafficSigns: "Trafik İşaretleri", practiceTests: "Pratik Testler", quickTips: "Hızlı İpuçları", importantWords: "Önemli Kelimeler", howToUse: "Nasıl Kullanılır", reviews: "Yorumlar", aiAssistant: "AI Asistan", prices: "Fiyatlar", settings: "Ayarlar", logout: "Çıkış", login: "Giriş", register: "Kayıt Ol", subscribersOnly: "Sadece Aboneler İçin", subscribeNow: "Şimdi Abone Ol", backToFreeTests: "Ücretsiz Testlere Dön", unlimitedQuestions: "Sınırsız soru", allTheoryLessons: "Tüm teori dersleri", tutorialVideos: "AI asistan", subscribeDesc: "Tüm derslere, trafik işaretlerine erişim için abone olun!", currentPlan: "Mevcut Planınız", sixMonths: "6 Ay", perMonth: "/ay", profile: "Profil", subscribed: "Abone", trial: "Deneme", subscribe: "Abone Ol", free: "Ücretsiz", startTest: "Teste Başla", forSubscribers: "Aboneler İçin", subscribeForAccess: "Tam erişim için abone olun!", later: "Daha Sonra", learnTrafficSigns: "Trafik işaretlerini öğrenin", goldenTips: "Test için altın ipuçları", demoAccount: "Demo Hesap", demoFullAccess: "Demo Hesap - Tam Erişim", timeRemaining: "Kalan süre", accountExpired: "Hesap süresi doldu", enjoyFeatures: "Tüm özelliklerin keyfini çıkarın!", upgradeNow: "Şimdi Yükselt", renewNow: "Şimdi Yenile", renewSubscription: "Aboneliği Şimdi Yenile", requestRefund: "İade Talep Et", refundUnderReview: "İade talebi inceleniyor", refundContactSoon: "Talebinizle ilgili yakında sizinle iletişime geçeceğiz.", refundReason: "İade nedeni", explainRefund: "Lütfen iade talebinizin nedenini açıklayın...", submitRequest: "Talebi Gönder", submitting: "Gönderiliyor...", refundAlreadyRequested: "İade zaten talep edildi", noActiveSubscription: "İade edilecek aktif abonelik yok" },
      footer: { privacy: "Gizlilik", terms: "Şartlar", refund: "İade", disclaimer: "Sorumluluk Reddi", cookies: "Çerezler" }
    }
  },
  // Persian/Farsi - فارسی
  fa: {
    translation: {
      nav: { home: "خانه", lessons: "دروس تئوری", trafficSigns: "علائم راهنمایی", tests: "آزمون‌های تمرینی", videos: "ویدیوها", assistant: "دستیار هوشمند", support: "پشتیبانی", pricing: "قیمت‌ها", login: "ورود", register: "ثبت‌نام", logout: "خروج", dashboard: "داشبورد" },
      hero: { title: "در آزمون تئوری موفق شوید!", subtitle: "تنها پلتفرم با ۹ زبان برای گواهینامه B سوئد. آزمون‌های تمرینی برای آمادگی در آزمون تئوری (kunskapsprov)، دروس تعاملی و دستیار هوشمند ۲۴ ساعته.", confidence: "تئوری گواهینامه B", startTrial: "سوال‌های رایگان را امتحان کن", learnMore: "بیشتر بدانید", trustedPlatform: "پلتفرم آموزشی معتبر", bLicense: "گواهینامه B", languageBadge: "در ۹ زبان موجود", studentsCount: "به بیش از ۱۰۰۰ دانش‌آموز بپیوندید" },
      howItWorks: { badge: "ساده و مؤثر", title: "۳ مرحله برای قبولی در آزمون تئوری", step1Title: "ثبت‌نام و شروع رایگان", step1Desc: "حساب بسازید و سوال را رایگان امتحان کنید.", step2Title: "به زبان خودتان تمرین کنید", step2Desc: "دروس تئوری را بخوانید و آزمون‌ها را به زبانی که بهتر می‌فهمید بدهید.", step3Title: "در آزمون تئوری قبول شوید", step3Desc: "سوالات ما برای کمک به آمادگی شما در آزمون تئوری (kunskapsprov) طراحی شده و برای راحتی شما به ۹ زبان در دسترس است." },
      features: { title: "همه چیز برای موفقیت", whyChooseUs: "چرا ما را انتخاب کنید", explore: "کاوش کنید", lessons: { title: "دروس تئوری", desc: "دروس جامع شامل همه قوانین راهنمایی و علائم." }, tests: { title: "آزمون‌های تمرینی", desc: "صدها سوال برای آمادگی در آزمون تئوری (kunskapsprov)." }, videos: { title: "آموزش ویدیویی", desc: "یادگیری تصویری با مربیان حرفه‌ای." }, ai: { title: "دستیار هوشمند", desc: "پاسخ فوری به سوالات رانندگی ۲۴/۷." } },
      pricing: { title: "قیمت‌گذاری شفاف", subtitle: "با سوال‌های رایگان شروع کنید.", monthly: "ماهانه", sixMonths: "۶ ماه", subscribe: "اشتراک", free: "رایگان", startFree: "شروع رایگان", popular: "محبوب", yourCurrentPlan: "پلن فعلی شما" },
      cta: { startJourney: "سفر خود به سوی موفقیت را امروز شروع کنید", joinStudents: "به هزاران دانشجو بپیوندید که با کمک ما در آزمون رانندگی قبول شدند" },
      auth: { login: "ورود", register: "ایجاد حساب", email: "ایمیل", password: "رمز عبور", name: "نام کامل", confirmPassword: "تأیید رمز عبور", forgotPassword: "رمز را فراموش کردید؟", noAccount: "حساب ندارید؟", hasAccount: "قبلاً حساب دارید؟", loginBtn: "ورود", registerBtn: "ثبت‌نام", or: "یا", googleSignIn: "ورود با Google", googleSignUp: "ثبت‌نام با Google", freeTrialQuestions: "با سوال‌های رایگان شروع کن", passwordMismatch: "رمزهای عبور مطابقت ندارند", passwordTooShort: "رمز عبور باید حداقل ۶ کاراکتر باشد", pleaseWait: "لطفاً صبر کنید" },
      dashboard: { welcome: "خوش آمدید", subscription: "اشتراک", trial: "آزمایشی", active: "فعال", expired: "منقضی", daysLeft: "روز باقیمانده", upgrade: "ارتقا" },
      tests: { title: "آزمون‌های تمرینی", startTest: "شروع آزمون", question: "سوال", of: "از", submit: "ارسال", next: "بعدی", previous: "قبلی", results: "نتایج", score: "نمره شما", correct: "صحیح", incorrect: "غلط", tryAgain: "دوباره", passed: "تبریک! قبول شدید!", failed: "به تمرین ادامه دهید!" },
      assistant: { title: "دستیار هوشمند", placeholder: "سوالی درباره رانندگی بپرسید...", send: "ارسال", thinking: "در حال فکر..." },
      support: { title: "پشتیبانی", subtitle: "ما اینجا برای کمک هستیم", name: "نام", email: "ایمیل", subject: "موضوع", message: "پیام", send: "ارسال", thank_you: "متشکرم!" },
      support_title: "پشتیبانی",
      support_subtitle: "ما اینجا برای کمک هستیم",
      support_form_title: "یک پیام برای ما ارسال کنید",
      support_form_subtitle: "ما در ۲۴ ساعت پاسخ می‌دهیم",
      support_name: "نام شما",
      support_email: "ایمیل شما",
      support_subject: "موضوع",
      support_message: "پیام",
      support_send: "ارسال پیام",
      support_thank_you: "متشکرم!",
      support_success_msg: "پیام شما ارسال شد! به زودی با شما تماس خواهیم گرفت.",
      support_send_another: "ارسال پیام دیگر",
      support_faq_title: "سوالات متداول",
      lessons_title: "دروس تئوری", tests_questions: "سوال", tests_minutes: "دقیقه", settings_title: "تنظیمات", settings_language: "زبان", settings_save: "ذخیره",
      back: "بازگشت", chooseTest: "یک آزمون انتخاب کنید", profile: "پروفایل", edit: "ویرایش", save: "ذخیره", cancel: "لغو", loading: "در حال بارگذاری...", noData: "داده‌ای نیست",
      dashboard_welcome: "خوش آمدید", dashboard_subscription: "اشتراک", dashboard_trial: "آزمایشی", dashboard_active: "فعال", dashboard_expired: "منقضی", dashboard_days_left: "روز باقیمانده", dashboard_upgrade: "ارتقا", dashboard_continue_learning: "ادامه یادگیری", dashboard_quick_actions: "اقدامات سریع", dashboard_lessons_title: "دروس تئوری", dashboard_lessons_desc: "قوانین ترافیک را یاد بگیرید", dashboard_tests_title: "آزمون‌های تمرینی", dashboard_tests_desc: "دانش خود را بسنجید", dashboard_videos_title: "ویدیوها", dashboard_videos_desc: "ببینید و یاد بگیرید", dashboard_assistant_title: "دستیار هوشمند", dashboard_assistant_desc: "هر سوالی بپرسید", dashboard_subtitle: "پیشرفت یادگیری شما",
      common: { loading: "در حال بارگذاری...", error: "خطایی رخ داد", success: "موفق", cancel: "لغو", save: "ذخیره", back: "بازگشت", delete: "حذف", edit: "ویرایش", add: "افزودن", search: "جستجو", next: "بعدی", previous: "قبلی", submit: "ارسال", close: "بستن", confirm: "تأیید", redirecting: "در حال هدایت...", returningToLogin: "بازگشت به صفحه ورود..." },
      subscription: { neverExpires: "اشتراک مادام‌العمر - هرگز منقضی نمی‌شود ✨", active: "فعال", expired: "منقضی", trial: "آزمایشی", lifetime: "مادام‌العمر" },
      settings: { expires: "تاریخ انقضا", trialEnds: "پایان آزمایشی", title: "تنظیمات", account: "حساب", language: "زبان" },
      ui: { overview: "نمای کلی", theoryLessons: "دروس تئوری", trafficSigns: "علائم راهنمایی", practiceTests: "آزمون‌های تمرینی", quickTips: "نکات سریع", importantWords: "کلمات مهم", howToUse: "نحوه استفاده", reviews: "نظرات", aiAssistant: "دستیار هوشمند", prices: "قیمت‌ها", settings: "تنظیمات", logout: "خروج", login: "ورود", register: "ثبت‌نام", subscribersOnly: "فقط برای مشترکین", subscribeNow: "همین حالا مشترک شوید", backToFreeTests: "بازگشت به آزمون‌های رایگان", unlimitedQuestions: "سوالات نامحدود", allTheoryLessons: "همه دروس تئوری", tutorialVideos: "دستیار هوشمند AI", subscribeDesc: "برای دسترسی به همه دروس و علائم مشترک شوید!", currentPlan: "پلن فعلی شما", sixMonths: "۶ ماه", perMonth: "/ماه", profile: "پروفایل", subscribed: "مشترک", trial: "آزمایشی", subscribe: "اشتراک", free: "رایگان", startTest: "شروع آزمون", forSubscribers: "برای مشترکین", subscribeForAccess: "برای دسترسی کامل مشترک شوید!", later: "بعداً", learnTrafficSigns: "علائم راهنمایی را یاد بگیرید", goldenTips: "نکات طلایی برای آزمون", demoAccount: "حساب دمو", demoFullAccess: "حساب دمو - دسترسی کامل", timeRemaining: "زمان باقیمانده", accountExpired: "حساب منقضی شده", enjoyFeatures: "از همه امکانات لذت ببرید!", upgradeNow: "همین حالا ارتقا دهید", renewNow: "همین حالا تمدید کنید", renewSubscription: "تمدید اشتراک", requestRefund: "درخواست بازپرداخت", refundUnderReview: "درخواست بازپرداخت در حال بررسی", refundContactSoon: "به زودی با شما تماس خواهیم گرفت.", refundReason: "دلیل بازپرداخت", explainRefund: "لطفاً دلیل درخواست بازپرداخت را توضیح دهید...", submitRequest: "ارسال درخواست", submitting: "در حال ارسال...", refundAlreadyRequested: "درخواست بازپرداخت قبلاً ثبت شده", noActiveSubscription: "اشتراک فعالی برای بازپرداخت وجود ندارد" },
      footer: { privacy: "حریم خصوصی", terms: "شرایط", refund: "بازپرداخت", disclaimer: "سلب مسئولیت", cookies: "کوکی‌ها" }
    }
  },
  // Somali - Soomaali
  so: {
    translation: {
      nav: { home: "Bogga Hore", lessons: "Casharada", trafficSigns: "Calaamadaha Taraafikada", tests: "Imtixaanada", videos: "Muuqaalada", assistant: "Kaaliyaha AI", support: "Taageero", pricing: "Qiimaha", login: "Soo gal", register: "Isdiiwaangeli", logout: "Ka bax", dashboard: "Dashboard" },
      hero: { title: "Ka Gudub Imtixaanka Aragtida Luqaddaada", subtitle: "Kaliya platform-ka 9 luqadood leh ee shatiga wadista Sweden B. Su'aalaha imtixaanka dhabta ah, casharo isdhexgal ah iyo caawiye AI 24/7.", confidence: "Aragtida Shatiga B", startTrial: "Su'aalo Bilaash ah", learnMore: "Wax badan Baro", trustedPlatform: "Platform Barashada oo lagu kalsoon yahay", bLicense: "Shatiga B", languageBadge: "Ku heli 9 luqadood", studentsCount: "Ku biir 800+ arday" },
      howItWorks: { badge: "Fudud & Wax ku ool", title: "3 Tallaabo si aad u gudbto Imtixaanka", step1Title: "Isdiiwaangeli & Bilow Bilaash", step1Desc: "Samee koontadaada oo tijaabi su'aalood oo bilaash ah.", step2Title: "Ku tababar Luqaddaada", step2Desc: "Baro casharada aragtida oo qaado imtixaanada luqadda aad si fiican u fahanto.", step3Title: "Ka gudub Imtixaanka Dhabta ah", step3Desc: "Su'aalahayagu waa la mid tahay imtixaanka dhabta ah, waxayna ku diyaarsan yihiin 9 luqadood." },
      features: { title: "Wax kasta oo aad u baahan tahay si aad u guuleysato", whyChooseUs: "Maxaad noo dooranaysaa", explore: "Sahaminta", lessons: { title: "Casharada Aragtida", desc: "Casharo dhammaystiran oo daboolaya dhammaan xeerarka taraafikada." }, tests: { title: "Imtixaanada Tababarka", desc: "Boqolaal su'aalood oo kuu diyaarinaya imtixaanka dhabta ah." }, videos: { title: "Casharada Muuqaalka", desc: "Barashada muuqaalka oo leh macallimiinta xirfadlaha ah." }, ai: { title: "Kaaliyaha AI", desc: "Hel jawaabo degdeg ah 24/7." } },
      pricing: { title: "Qiime Fudud", subtitle: "Ku bilow su'aalood oo bilaash ah.", monthly: "Bishiiba", sixMonths: "6 Bilood", subscribe: "Isdiiwaangeli", free: "Bilaash", startFree: "Bilow Bilaash", popular: "Caanka ah", yourCurrentPlan: "Qorshaha hadda" },
      cta: { startJourney: "Maanta ku bilow safarkaaga guusha", joinStudents: "Ku biir kumanaan arday oo ku guuleystay imtixaanka wadista caawimaadeena" },
      auth: { login: "Soo gal", register: "Samee Xisaab", email: "Iimaylka", password: "Furaha", name: "Magaca", confirmPassword: "Xaqiiji Furaha", forgotPassword: "Furaha ma ilowday?", noAccount: "Xisaab ma lihid?", hasAccount: "Xisaab ayaad haysataa?", loginBtn: "Soo gal", registerBtn: "Isdiiwaangeli", or: "ama", googleSignIn: "Ku soo gal Google", googleSignUp: "Ku isdiiwaangeli Google", freeTrialQuestions: "Ku bilow su'aalo bilaash ah", passwordMismatch: "Fureyaashu ma is waafaqaan", passwordTooShort: "Furaha waa inuu ahaadaa ugu yaraan 6 xaraf", pleaseWait: "Fadlan sug" },
      dashboard: { welcome: "Ku soo dhawoow", subscription: "Isdiiwaangeli", trial: "Tijaabo", active: "Firfircoon", expired: "Dhacay", daysLeft: "maalmood hadhay", upgrade: "Kor u qaad" },
      tests: { title: "Imtixaanada", startTest: "Bilow", question: "Su'aal", of: "ka mid ah", submit: "Dir", next: "Xiga", previous: "Hore", results: "Natiijooyinka", score: "Dhibcahaaga", correct: "Sax", incorrect: "Qalad", tryAgain: "Mar kale isku day", passed: "Hambalyo! Waad guuleysatay!", failed: "Sii wad tababarka!" },
      assistant: { title: "Kaaliyaha AI", placeholder: "Wax ka weydii wadista...", send: "Dir", thinking: "Waa fikiraya..." },
      support: { title: "Taageero", subtitle: "Waxaan halkaan u joognaa inaan ku caawino", name: "Magacaaga", email: "Iimaylkaaga", subject: "Mawduuca", message: "Fariinta", send: "Dir", thank_you: "Mahadsanid!" },
      support_title: "Taageero",
      support_subtitle: "Waxaan halkaan u joognaa inaan ku caawino",
      support_form_title: "Noo soo dir fariin",
      support_form_subtitle: "Waxaan ka jawaabnaynaa 24 saacadood gudahooda",
      support_name: "Magacaaga",
      support_email: "Iimaylkaaga",
      support_subject: "Mawduuca",
      support_message: "Fariinta",
      support_send: "Dir Fariinta",
      support_thank_you: "Mahadsanid!",
      support_success_msg: "Fariintaada waa la diray! Dhawaan waan kula soo xiriiri doonaa.",
      support_send_another: "Dir fariin kale",
      support_faq_title: "Su'aalaha Badanaa La Isweydiiyo",
      lessons_title: "Casharada", tests_questions: "su'aalood", tests_minutes: "daqiiqo", settings_title: "Dejinta", settings_language: "Luuqada", settings_save: "Kaydi",
      dashboard_welcome: "Ku soo dhawoow", dashboard_subscription: "Isdiiwaangeli", dashboard_trial: "Tijaabo", dashboard_active: "Firfircoon", dashboard_expired: "Dhacay", dashboard_days_left: "maalmood hadhay", dashboard_upgrade: "Kor u qaad", dashboard_continue_learning: "Sii wad barashada", dashboard_quick_actions: "Ficilada Degdegga", dashboard_lessons_title: "Casharada", dashboard_lessons_desc: "Baro xeerarka taraafikada", dashboard_tests_title: "Imtixaanada", dashboard_tests_desc: "Tijaabi aqoontaada", dashboard_videos_title: "Muuqaalada", dashboard_videos_desc: "Daaw oo baro", dashboard_assistant_title: "Kaaliyaha AI", dashboard_assistant_desc: "Weydii su'aal kasta", dashboard_subtitle: "Halkan waa horumarkaaga barashada",
      common: { loading: "Waa soo raraya...", error: "Khalad ayaa dhacay", success: "Guul", cancel: "Ka noqo", save: "Kaydi", back: "Dib u noqo", delete: "Tirtir", edit: "Wax ka bedel", add: "Ku dar", search: "Raadi", next: "Xiga", previous: "Hore", submit: "Dir", close: "Xir", confirm: "Xaqiiji", redirecting: "Waa la dambaabinaayo...", returningToLogin: "Ku laabashada galitaanka..." },
      subscription: { neverExpires: "Isdiiwaangeli nolosha oo dhan - weligeed ma dhacdo ✨", active: "Firfircoon", expired: "Dhacay", trial: "Tijaabo", lifetime: "Nolosha oo dhan" },
      settings: { expires: "Dhamaad", trialEnds: "Tijaabo dhamaad", title: "Dejinta", account: "Xisaab", language: "Luuqada" },
      ui: { overview: "Dulmar", theoryLessons: "Casharada Aragtida", trafficSigns: "Calaamadaha Taraafikada", practiceTests: "Imtixaanada Tababarka", quickTips: "Talooyinka Degdegga", importantWords: "Erayada Muhiimka", howToUse: "Sida loo isticmaalo", reviews: "Dib-u-eegis", aiAssistant: "Kaaliyaha AI", prices: "Qiimaha", settings: "Dejinta", logout: "Ka bax", login: "Soo gal", register: "Isdiiwaangeli", subscribersOnly: "Oo keliya dadka isdiiwaangeliyey", subscribeNow: "Hadda isdiiwaangeli", backToFreeTests: "Ku noqo imtixaanada bilaashka ah", unlimitedQuestions: "Su'aalo aan xad lahayn", allTheoryLessons: "Dhammaan casharada aragtida", tutorialVideos: "Kaaliyaha AI", subscribeDesc: "Isdiiwaangeli si aad u hesho dhammaan casharada, calaamadaha!", currentPlan: "Qorshahaa hadda", sixMonths: "6 Bilood", perMonth: "/bishii", profile: "Xogta shaqsiga", subscribed: "Isdiiwaangeliyey", trial: "Tijaabo", subscribe: "Isdiiwaangeli", free: "Bilaash", startTest: "Bilow imtixaanka", forSubscribers: "Dadka isdiiwaangeliyey", subscribeForAccess: "Isdiiwaangeli si aad u hesho!", later: "Dabadeed", learnTrafficSigns: "Baro calaamadaha taraafikada", goldenTips: "Talooyinka dahabiga ah", demoAccount: "Xisaab tijaabo", demoFullAccess: "Xisaab tijaabo - Gelitaan buuxa", timeRemaining: "Waqtiga hadhay", accountExpired: "Xisaabta waa dhacday", enjoyFeatures: "Ku raaxayso dhammaan astaamaha!", upgradeNow: "Hadda kor u qaad", renewNow: "Hadda cusbooneysii", renewSubscription: "Cusbooneysii isdiiwaangelintu", requestRefund: "Codso lacag celin", refundUnderReview: "Codsiga lacag celinta waa la eegayaa", refundContactSoon: "Dhawaan waan kula soo xiriiri doonaa.", refundReason: "Sababta lacag celinta", explainRefund: "Fadlan sharax sababta aad u codsanaysid...", submitRequest: "Dir codsiga", submitting: "Waa diraya...", refundAlreadyRequested: "Lacag celinta horey ayaa la codsaday", noActiveSubscription: "Ma jiro isdiiwaangelin firfircoon oo la celiyo" },
      footer: { privacy: "Asturnaanta", terms: "Shuruudaha", refund: "Lacag celin", disclaimer: "Ka-fogaanshaha mas'uuliyadda", cookies: "Cookies" }
    }
  },
  // Polish - Polski
  pl: {
    translation: {
      nav: { home: "Strona główna", lessons: "Lekcje teorii", trafficSigns: "Znaki drogowe", tests: "Testy praktyczne", videos: "Wideo", assistant: "Asystent AI", support: "Wsparcie", pricing: "Cennik", login: "Zaloguj", register: "Zarejestruj", logout: "Wyloguj", dashboard: "Panel" },
      hero: { title: "Zdaj egzamin teoretyczny w swoim języku", subtitle: "Jedyna platforma w 9 językach na szwedzkie prawo jazdy kat. B. Testy identyczne z prawdziwym egzaminem, lekcje i asystent AI 24/7.", confidence: "Teoria kat. B", startTrial: "Wypróbuj darmowe pytania", learnMore: "Dowiedz się więcej", trustedPlatform: "Zaufana platforma edukacyjna", bLicense: "Prawo jazdy kat. B", languageBadge: "Dostępne w 9 językach", studentsCount: "Dołącz do 800+ uczniów" },
      howItWorks: { badge: "Prosto i Skutecznie", title: "3 Kroki do Zdania Egzaminu", step1Title: "Zarejestruj się i Zacznij Za Darmo", step1Desc: "Utwórz konto i wypróbuj pytania za darmo.", step2Title: "Ćwicz w Swoim Języku", step2Desc: "Ucz się teorii i rozwiązuj testy w języku, który najlepiej rozumiesz.", step3Title: "Zdaj Prawdziwy Egzamin", step3Desc: "Nasze pytania są identyczne z prawdziwym egzaminem, dostępne w 9 językach dla Twojej wygody." },
      features: { title: "Wszystko czego potrzebujesz", whyChooseUs: "Dlaczego my", explore: "Odkryj", lessons: { title: "Lekcje teorii", desc: "Kompleksowe lekcje obejmujące wszystkie przepisy ruchu drogowego." }, tests: { title: "Testy praktyczne", desc: "Setki pytań przygotowujących do teoretycznego egzaminu na prawo jazdy (kunskapsprov)." }, videos: { title: "Lekcje wideo", desc: "Nauka wizualna z profesjonalnymi instruktorami." }, ai: { title: "Asystent AI", desc: "Natychmiastowe odpowiedzi na pytania 24/7." } },
      pricing: { title: "Proste ceny", subtitle: "Zacznij od darmowe pytania.", monthly: "Miesięcznie", sixMonths: "6 miesięcy", subscribe: "Subskrybuj", free: "Bezpłatnie", startFree: "Zacznij za darmo", popular: "Popularne", yourCurrentPlan: "Twój obecny plan" },
      cta: { startJourney: "Rozpocznij swoją drogę do sukcesu już dziś", joinStudents: "Dołącz do tysięcy uczniów, którzy zdali egzamin na prawo jazdy z naszą pomocą" },
      auth: { login: "Zaloguj", register: "Utwórz konto", email: "E-mail", password: "Hasło", name: "Imię i nazwisko", confirmPassword: "Potwierdź hasło", forgotPassword: "Zapomniałeś hasła?", noAccount: "Nie masz konta?", hasAccount: "Masz już konto?", loginBtn: "Zaloguj się", registerBtn: "Zarejestruj się", or: "lub", googleSignIn: "Zaloguj przez Google", googleSignUp: "Zarejestruj przez Google", freeTrialQuestions: "Zacznij za darmo z pytaniami próbnymi", passwordMismatch: "Hasła nie są zgodne", passwordTooShort: "Hasło musi mieć co najmniej 6 znaków", pleaseWait: "Proszę czekać" },
      dashboard: { welcome: "Witaj ponownie", subscription: "Subskrypcja", trial: "Wersja próbna", active: "Aktywna", expired: "Wygasła", daysLeft: "dni pozostało", upgrade: "Ulepsz" },
      tests: { title: "Testy", startTest: "Rozpocznij test", question: "Pytanie", of: "z", submit: "Wyślij", next: "Następne", previous: "Poprzednie", results: "Wyniki", score: "Twój wynik", correct: "Poprawne", incorrect: "Błędne", tryAgain: "Spróbuj ponownie", passed: "Gratulacje! Zdałeś!", failed: "Ćwicz dalej!" },
      assistant: { title: "Asystent AI", placeholder: "Zapytaj o jazdę...", send: "Wyślij", thinking: "Myślę..." },
      support: { title: "Wsparcie", subtitle: "Jesteśmy tu, aby pomóc", name: "Twoje imię", email: "Twój e-mail", subject: "Temat", message: "Wiadomość", send: "Wyślij", thank_you: "Dziękujemy!" },
      support_title: "Wsparcie",
      support_subtitle: "Jesteśmy tu, aby pomóc",
      support_form_title: "Wyślij nam wiadomość",
      support_form_subtitle: "Odpowiadamy w ciągu 24 godzin",
      support_name: "Twoje imię",
      support_email: "Twój e-mail",
      support_subject: "Temat",
      support_message: "Wiadomość",
      support_send: "Wyślij wiadomość",
      support_thank_you: "Dziękujemy!",
      support_success_msg: "Twoja wiadomość została wysłana! Wkrótce się z Tobą skontaktujemy.",
      support_send_another: "Wyślij kolejną wiadomość",
      support_faq_title: "Często zadawane pytania",
      lessons_title: "Lekcje teorii", tests_questions: "pytań", tests_minutes: "minut", settings_title: "Ustawienia", settings_language: "Język", settings_save: "Zapisz",
      dashboard_welcome: "Witaj ponownie", dashboard_subscription: "Subskrypcja", dashboard_trial: "Wersja próbna", dashboard_active: "Aktywna", dashboard_expired: "Wygasła", dashboard_days_left: "dni pozostało", dashboard_upgrade: "Ulepsz", dashboard_continue_learning: "Kontynuuj naukę", dashboard_quick_actions: "Szybkie akcje", dashboard_lessons_title: "Lekcje teorii", dashboard_lessons_desc: "Naucz się przepisów", dashboard_tests_title: "Testy praktyczne", dashboard_tests_desc: "Sprawdź swoją wiedzę", dashboard_videos_title: "Lekcje wideo", dashboard_videos_desc: "Oglądaj i ucz się", dashboard_assistant_title: "Asystent AI", dashboard_assistant_desc: "Zadaj pytanie", dashboard_subtitle: "Oto twój postęp w nauce",
      common: { loading: "Ładowanie...", error: "Wystąpił błąd", success: "Sukces", cancel: "Anuluj", save: "Zapisz", back: "Wstecz", delete: "Usuń", edit: "Edytuj", add: "Dodaj", search: "Szukaj", next: "Następny", previous: "Poprzedni", submit: "Wyślij", close: "Zamknij", confirm: "Potwierdź", redirecting: "Przekierowywanie...", returningToLogin: "Powrót do logowania..." },
      subscription: { neverExpires: "Dożywotnia subskrypcja - nigdy nie wygasa ✨", active: "Aktywna", expired: "Wygasła", trial: "Próbna", lifetime: "Dożywotnia" },
      settings: { expires: "Wygasa", trialEnds: "Próba kończy się", title: "Ustawienia", account: "Konto", language: "Język" },
      ui: { overview: "Przegląd", theoryLessons: "Lekcje teorii", trafficSigns: "Znaki drogowe", practiceTests: "Testy praktyczne", quickTips: "Szybkie porady", importantWords: "Ważne Słowa", howToUse: "Jak korzystać", reviews: "Recenzje", aiAssistant: "Asystent AI", prices: "Cennik", settings: "Ustawienia", logout: "Wyloguj", login: "Zaloguj", register: "Zarejestruj", subscribersOnly: "Tylko dla subskrybentów", subscribeNow: "Subskrybuj teraz", backToFreeTests: "Wróć do darmowych testów", unlimitedQuestions: "Nieograniczone pytania", allTheoryLessons: "Wszystkie lekcje teorii", tutorialVideos: "Asystent AI", subscribeDesc: "Subskrybuj, aby uzyskać dostęp do wszystkich lekcji i znaków!", currentPlan: "Twój obecny plan", sixMonths: "6 miesięcy", perMonth: "/miesiąc", profile: "Profil", subscribed: "Subskrybent", trial: "Próbna", subscribe: "Subskrybuj", free: "Bezpłatnie", startTest: "Rozpocznij test", forSubscribers: "Dla subskrybentów", subscribeForAccess: "Subskrybuj dla pełnego dostępu!", later: "Później", learnTrafficSigns: "Naucz się znaków drogowych", goldenTips: "Złote porady do egzaminu", demoAccount: "Konto demo", demoFullAccess: "Konto demo - Pełny dostęp", timeRemaining: "Pozostały czas", accountExpired: "Konto wygasło", enjoyFeatures: "Korzystaj ze wszystkich funkcji!", upgradeNow: "Ulepsz teraz", renewNow: "Odnów teraz", renewSubscription: "Odnów subskrypcję teraz", requestRefund: "Poproś o zwrot", refundUnderReview: "Wniosek o zwrot w trakcie weryfikacji", refundContactSoon: "Wkrótce się z Tobą skontaktujemy.", refundReason: "Powód zwrotu", explainRefund: "Wyjaśnij powód prośby o zwrot...", submitRequest: "Wyślij wniosek", submitting: "Wysyłanie...", refundAlreadyRequested: "Zwrot już został poproszony", noActiveSubscription: "Brak aktywnej subskrypcji do zwrotu" },
      footer: { privacy: "Prywatność", terms: "Regulamin", refund: "Zwrot", disclaimer: "Wyłączenie odpowiedzialności", cookies: "Ciasteczka" }
    }
  },
  // Spanish - Español
  es: {
    translation: {
      nav: { home: "Inicio", lessons: "Lecciones", trafficSigns: "Señales de tráfico", tests: "Tests", videos: "Videos", assistant: "Asistente IA", support: "Soporte", pricing: "Precios", login: "Iniciar sesión", register: "Registrarse", logout: "Cerrar sesión", dashboard: "Panel" },
      hero: { title: "Aprueba la teoría de conducción en tu idioma", subtitle: "La única plataforma con 9 idiomas para el permiso de conducir sueco B. Tests de práctica para prepararte para el examen teórico (kunskapsprov), lecciones interactivas y asistente IA 24/7.", confidence: "Teoría permiso B", startTrial: "Prueba preguntas gratis", learnMore: "Saber más", trustedPlatform: "Plataforma de aprendizaje confiable", bLicense: "Permiso B", languageBadge: "Disponible en 9 idiomas", studentsCount: "Únete a más de 1000 estudiantes" },
      howItWorks: { badge: "Simple y Eficaz", title: "3 Pasos para Aprobar el Examen Teórico", step1Title: "Regístrate y Empieza Gratis", step1Desc: "Crea tu cuenta y prueba preguntas gratis — sin pago.", step2Title: "Practica en Tu Idioma", step2Desc: "Estudia las lecciones y haz tests en el idioma que mejor entiendas.", step3Title: "Aprueba el Examen Teórico", step3Desc: "Nuestras preguntas están diseñadas para ayudarte a prepararte para el examen teórico (kunskapsprov), disponibles en 9 idiomas para tu comodidad." },
      features: { title: "Todo lo que necesitas para aprobar", whyChooseUs: "Por qué elegirnos", explore: "Explorar", lessons: { title: "Lecciones de teoría", desc: "Lecciones completas que cubren todas las normas de tráfico." }, tests: { title: "Tests de práctica", desc: "Cientos de preguntas para prepararte para el examen teórico (kunskapsprov)." }, videos: { title: "Videos tutoriales", desc: "Aprendizaje visual con instructores profesionales." }, ai: { title: "Asistente IA", desc: "Respuestas instantáneas a tus preguntas 24/7." } },
      pricing: { title: "Precios simples", subtitle: "Comienza con preguntas gratis.", monthly: "Mensual", sixMonths: "6 meses", subscribe: "Suscribirse", free: "Gratis", startFree: "Empezar gratis", popular: "Popular", yourCurrentPlan: "Tu plan actual" },
      cta: { startJourney: "Comienza tu camino hacia el éxito hoy", joinStudents: "Únete a miles de estudiantes que han aprobado sus exámenes de conducir con nuestra ayuda" },
      auth: { login: "Iniciar sesión", register: "Crear cuenta", email: "Correo", password: "Contraseña", name: "Nombre completo", confirmPassword: "Confirmar contraseña", forgotPassword: "¿Olvidaste tu contraseña?", noAccount: "¿No tienes cuenta?", hasAccount: "¿Ya tienes cuenta?", loginBtn: "Entrar", registerBtn: "Registrarse", or: "o", googleSignIn: "Iniciar sesión con Google", googleSignUp: "Registrarse con Google", freeTrialQuestions: "Empieza gratis con preguntas de práctica", passwordMismatch: "Las contraseñas no coinciden", passwordTooShort: "La contraseña debe tener al menos 6 caracteres", pleaseWait: "Por favor espere" },
      dashboard: { welcome: "Bienvenido de nuevo", subscription: "Suscripción", trial: "Prueba gratuita", active: "Activa", expired: "Expirada", daysLeft: "días restantes", upgrade: "Mejorar" },
      tests: { title: "Tests", startTest: "Iniciar test", question: "Pregunta", of: "de", submit: "Enviar", next: "Siguiente", previous: "Anterior", results: "Resultados", score: "Tu puntuación", correct: "Correctas", incorrect: "Incorrectas", tryAgain: "Intentar de nuevo", passed: "¡Felicidades! ¡Aprobaste!", failed: "¡Sigue practicando!" },
      assistant: { title: "Asistente IA", placeholder: "Pregunta sobre conducción...", send: "Enviar", thinking: "Pensando..." },
      support: { title: "Soporte", subtitle: "Estamos aquí para ayudarte", name: "Tu nombre", email: "Tu correo", subject: "Asunto", message: "Mensaje", send: "Enviar", thank_you: "¡Gracias!" },
      support_title: "Soporte",
      support_subtitle: "Estamos aquí para ayudarte",
      support_form_title: "Envíanos un mensaje",
      support_form_subtitle: "Respondemos en 24 horas",
      support_name: "Tu nombre",
      support_email: "Tu correo",
      support_subject: "Asunto",
      support_message: "Mensaje",
      support_send: "Enviar mensaje",
      support_thank_you: "¡Gracias!",
      support_success_msg: "¡Tu mensaje ha sido enviado! Nos pondremos en contacto pronto.",
      support_send_another: "Enviar otro mensaje",
      support_faq_title: "Preguntas frecuentes",
      lessons_title: "Lecciones de teoría", tests_questions: "preguntas", tests_minutes: "minutos", settings_title: "Configuración", settings_language: "Idioma", settings_save: "Guardar",
      dashboard_welcome: "Bienvenido de nuevo", dashboard_subscription: "Suscripción", dashboard_trial: "Prueba gratuita", dashboard_active: "Activa", dashboard_expired: "Expirada", dashboard_days_left: "días restantes", dashboard_upgrade: "Mejorar", dashboard_continue_learning: "Continuar aprendiendo", dashboard_quick_actions: "Acciones rápidas", dashboard_lessons_title: "Lecciones de teoría", dashboard_lessons_desc: "Aprende las normas de tráfico", dashboard_tests_title: "Tests de práctica", dashboard_tests_desc: "Pon a prueba tus conocimientos", dashboard_videos_title: "Videos", dashboard_videos_desc: "Mira y aprende", dashboard_assistant_title: "Asistente IA", dashboard_assistant_desc: "Haz cualquier pregunta", dashboard_subtitle: "Aquí está tu progreso de aprendizaje",
      common: { loading: "Cargando...", error: "Ocurrió un error", success: "Éxito", cancel: "Cancelar", save: "Guardar", back: "Atrás", delete: "Eliminar", edit: "Editar", add: "Añadir", search: "Buscar", next: "Siguiente", previous: "Anterior", submit: "Enviar", close: "Cerrar", confirm: "Confirmar", redirecting: "Redirigiendo...", returningToLogin: "Volviendo al inicio de sesión..." },
      subscription: { neverExpires: "Suscripción vitalicia - nunca expira ✨", active: "Activa", expired: "Expirada", trial: "Prueba", lifetime: "Vitalicia" },
      settings: { expires: "Expira", trialEnds: "Prueba termina", title: "Configuración", account: "Cuenta", language: "Idioma" },
      ui: { overview: "Resumen", theoryLessons: "Lecciones de teoría", trafficSigns: "Señales de tráfico", practiceTests: "Tests de práctica", quickTips: "Consejos rápidos", importantWords: "Palabras Importantes", howToUse: "Cómo usar", reviews: "Reseñas", aiAssistant: "Asistente IA", prices: "Precios", settings: "Configuración", logout: "Cerrar sesión", login: "Iniciar sesión", register: "Registrarse", subscribersOnly: "Solo para suscriptores", subscribeNow: "Suscríbete ahora", backToFreeTests: "Volver a tests gratuitos", unlimitedQuestions: "Preguntas ilimitadas", allTheoryLessons: "Todas las lecciones de teoría", tutorialVideos: "Asistente IA", subscribeDesc: "¡Suscríbete para acceder a todas las lecciones y señales!", currentPlan: "Tu plan actual", sixMonths: "6 meses", perMonth: "/mes", profile: "Perfil", subscribed: "Suscriptor", trial: "Prueba", subscribe: "Suscribirse", free: "Gratis", startTest: "Iniciar test", forSubscribers: "Para suscriptores", subscribeForAccess: "¡Suscríbete para acceso completo!", later: "Más tarde", learnTrafficSigns: "Aprende señales de tráfico", goldenTips: "Consejos de oro para el examen", demoAccount: "Cuenta demo", demoFullAccess: "Cuenta demo - Acceso completo", timeRemaining: "Tiempo restante", accountExpired: "Cuenta expirada", enjoyFeatures: "¡Disfruta de todas las funciones!", upgradeNow: "Mejorar ahora", renewNow: "Renovar ahora", renewSubscription: "Renovar suscripción ahora", requestRefund: "Solicitar reembolso", refundUnderReview: "Solicitud de reembolso en revisión", refundContactSoon: "Nos pondremos en contacto pronto.", refundReason: "Razón del reembolso", explainRefund: "Explica por qué solicitas el reembolso...", submitRequest: "Enviar solicitud", submitting: "Enviando...", refundAlreadyRequested: "Reembolso ya solicitado", noActiveSubscription: "No hay suscripción activa para reembolsar" },
      footer: { privacy: "Privacidad", terms: "Términos", refund: "Reembolso", disclaimer: "Descargo de responsabilidad", cookies: "Cookies" }
    }
  },
  // German - Deutsch
  de: {
    translation: {
      nav: { home: "Startseite", lessons: "Theorie-Lektionen", trafficSigns: "Verkehrszeichen", tests: "Übungstests", videos: "Videos", assistant: "KI-Assistent", support: "Support", pricing: "Preise", login: "Anmelden", register: "Registrieren", logout: "Abmelden", dashboard: "Dashboard" },
      hero: { title: "Bestehe die Fahrtheorie in deiner Sprache", subtitle: "Die einzige Plattform mit 9 Sprachen für den schwedischen B-Führerschein. Übungstests zur Vorbereitung auf die Theorieprüfung (kunskapsprov), interaktive Lektionen und KI-Assistent rund um die Uhr.", confidence: "B-Führerschein Theorie", startTrial: "Kostenlose Fragen testen", learnMore: "Mehr erfahren", trustedPlatform: "Vertrauenswürdige Lernplattform", bLicense: "B-Führerschein", languageBadge: "In 9 Sprachen verfügbar", studentsCount: "Schließe dich 800+ Schülern an" },
      howItWorks: { badge: "Einfach & Effektiv", title: "3 Schritte zum Bestehen der Theorieprüfung", step1Title: "Registriere dich & Starte Kostenlos", step1Desc: "Erstelle dein Konto und teste Fragen kostenlos.", step2Title: "Übe in Deiner Sprache", step2Desc: "Lerne Theorie und mache Tests in der Sprache, die du am besten verstehst.", step3Title: "Bestehe die Echte Prüfung", step3Desc: "Unsere Fragen spiegeln die echte Prüfung wider, verfügbar in 9 Sprachen für deinen Komfort." },
      features: { title: "Alles was Sie zum Bestehen brauchen", whyChooseUs: "Warum uns wählen", explore: "Entdecken", lessons: { title: "Theorie-Lektionen", desc: "Umfassende Lektionen zu allen Verkehrsregeln und Zeichen." }, tests: { title: "Übungstests", desc: "Hunderte Fragen zur Vorbereitung auf die echte Prüfung." }, videos: { title: "Video-Tutorials", desc: "Visuelles Lernen mit professionellen Fahrlehrern." }, ai: { title: "KI-Assistent", desc: "Sofortige Antworten auf Ihre Fragen 24/7." } },
      pricing: { title: "Einfache Preise", subtitle: "Starten Sie mit kostenlosen Fragen.", monthly: "Monatlich", sixMonths: "6 Monate", subscribe: "Abonnieren", free: "Kostenlos", startFree: "Kostenlos starten", popular: "Beliebt", yourCurrentPlan: "Ihr aktueller Plan" },
      cta: { startJourney: "Starten Sie heute Ihre Reise zum Erfolg", joinStudents: "Schließen Sie sich Tausenden von Schülern an, die ihre Fahrprüfung mit unserer Hilfe bestanden haben" },
      auth: { login: "Anmelden", register: "Konto erstellen", email: "E-Mail", password: "Passwort", name: "Vollständiger Name", confirmPassword: "Passwort bestätigen", forgotPassword: "Passwort vergessen?", noAccount: "Kein Konto?", hasAccount: "Bereits ein Konto?", loginBtn: "Anmelden", registerBtn: "Registrieren", or: "oder", googleSignIn: "Mit Google anmelden", googleSignUp: "Mit Google registrieren", freeTrialQuestions: "Starte kostenlos mit Übungsfragen", passwordMismatch: "Passwörter stimmen nicht überein", passwordTooShort: "Passwort muss mindestens 6 Zeichen haben", pleaseWait: "Bitte warten" },
      dashboard: { welcome: "Willkommen zurück", subscription: "Abonnement", trial: "Testversion", active: "Aktiv", expired: "Abgelaufen", daysLeft: "Tage übrig", upgrade: "Upgraden" },
      tests: { title: "Tests", startTest: "Test starten", question: "Frage", of: "von", submit: "Absenden", next: "Weiter", previous: "Zurück", results: "Ergebnisse", score: "Ihre Punktzahl", correct: "Richtig", incorrect: "Falsch", tryAgain: "Erneut versuchen", passed: "Herzlichen Glückwunsch! Bestanden!", failed: "Weiter üben!" },
      assistant: { title: "KI-Assistent", placeholder: "Fragen Sie etwas über das Fahren...", send: "Senden", thinking: "Denke nach..." },
      support: { title: "Support", subtitle: "Wir sind hier um zu helfen", name: "Ihr Name", email: "Ihre E-Mail", subject: "Betreff", message: "Nachricht", send: "Senden", thank_you: "Danke!" },
      support_title: "Support",
      support_subtitle: "Wir sind hier um zu helfen",
      support_form_title: "Senden Sie uns eine Nachricht",
      support_form_subtitle: "Wir antworten innerhalb von 24 Stunden",
      support_name: "Ihr Name",
      support_email: "Ihre E-Mail",
      support_subject: "Betreff",
      support_message: "Nachricht",
      support_send: "Nachricht senden",
      support_thank_you: "Danke!",
      support_success_msg: "Ihre Nachricht wurde gesendet! Wir werden uns bald bei Ihnen melden.",
      support_send_another: "Weitere Nachricht senden",
      support_faq_title: "Häufig gestellte Fragen",
      lessons_title: "Theorie-Lektionen", tests_questions: "Fragen", tests_minutes: "Minuten", settings_title: "Einstellungen", settings_language: "Sprache", settings_save: "Speichern",
      dashboard_welcome: "Willkommen zurück", dashboard_subscription: "Abonnement", dashboard_trial: "Testversion", dashboard_active: "Aktiv", dashboard_expired: "Abgelaufen", dashboard_days_left: "Tage übrig", dashboard_upgrade: "Upgraden", dashboard_continue_learning: "Weiter lernen", dashboard_quick_actions: "Schnellaktionen", dashboard_lessons_title: "Theorie-Lektionen", dashboard_lessons_desc: "Verkehrsregeln lernen", dashboard_tests_title: "Übungstests", dashboard_tests_desc: "Wissen testen", dashboard_videos_title: "Videos", dashboard_videos_desc: "Ansehen und lernen", dashboard_assistant_title: "KI-Assistent", dashboard_assistant_desc: "Fragen Sie alles", dashboard_subtitle: "Hier ist Ihr Lernfortschritt",
      common: { loading: "Laden...", error: "Ein Fehler ist aufgetreten", success: "Erfolg", cancel: "Abbrechen", save: "Speichern", back: "Zurück", delete: "Löschen", edit: "Bearbeiten", add: "Hinzufügen", search: "Suchen", next: "Weiter", previous: "Zurück", submit: "Absenden", close: "Schließen", confirm: "Bestätigen", redirecting: "Weiterleitung...", returningToLogin: "Zurück zur Anmeldung..." },
      subscription: { neverExpires: "Lebenslange Mitgliedschaft - läuft nie ab ✨", active: "Aktiv", expired: "Abgelaufen", trial: "Testversion", lifetime: "Lebenslang" },
      settings: { expires: "Läuft ab", trialEnds: "Testversion endet", title: "Einstellungen", account: "Konto", language: "Sprache" },
      ui: { overview: "Übersicht", theoryLessons: "Theorie-Lektionen", trafficSigns: "Verkehrszeichen", practiceTests: "Übungstests", quickTips: "Schnelle Tipps", importantWords: "Wichtige Wörter", howToUse: "Anleitung", reviews: "Bewertungen", aiAssistant: "KI-Assistent", prices: "Preise", settings: "Einstellungen", logout: "Abmelden", login: "Anmelden", register: "Registrieren", subscribersOnly: "Nur für Abonnenten", subscribeNow: "Jetzt abonnieren", backToFreeTests: "Zurück zu kostenlosen Tests", unlimitedQuestions: "Unbegrenzte Fragen", allTheoryLessons: "Alle Theorie-Lektionen", tutorialVideos: "KI-Assistent", subscribeDesc: "Abonnieren Sie für Zugang zu allen Lektionen und Zeichen!", currentPlan: "Ihr aktueller Plan", sixMonths: "6 Monate", perMonth: "/Monat", profile: "Profil", subscribed: "Abonnent", trial: "Testversion", subscribe: "Abonnieren", free: "Kostenlos", startTest: "Test starten", forSubscribers: "Für Abonnenten", subscribeForAccess: "Abonnieren für vollen Zugang!", later: "Später", learnTrafficSigns: "Verkehrszeichen lernen", goldenTips: "Goldene Tipps für die Prüfung", demoAccount: "Demo-Konto", demoFullAccess: "Demo-Konto - Voller Zugang", timeRemaining: "Verbleibende Zeit", accountExpired: "Konto abgelaufen", enjoyFeatures: "Genießen Sie alle Funktionen!", upgradeNow: "Jetzt upgraden", renewNow: "Jetzt verlängern", renewSubscription: "Abonnement jetzt verlängern", requestRefund: "Rückerstattung anfordern", refundUnderReview: "Rückerstattungsantrag wird geprüft", refundContactSoon: "Wir werden Sie bald kontaktieren.", refundReason: "Grund für Rückerstattung", explainRefund: "Bitte erklären Sie den Grund für die Rückerstattung...", submitRequest: "Anfrage senden", submitting: "Wird gesendet...", refundAlreadyRequested: "Rückerstattung bereits beantragt", noActiveSubscription: "Kein aktives Abonnement zur Rückerstattung" },
      footer: { privacy: "Datenschutz", terms: "AGB", refund: "Rückerstattung", disclaimer: "Haftungsausschluss", cookies: "Cookies" }
    }
  }
};

// Detect language from URL path SYNCHRONOUSLY so i18n init uses the correct
// language on the very first render. Prevents a font/direction swap flash.
const _supportedLangs = ['ar', 'en', 'sv', 'tr', 'fa', 'so', 'pl', 'es', 'de'];
const _pathLang = (typeof window !== 'undefined')
  ? (window.location.pathname.split('/').filter(Boolean)[0] || '')
  : '';
// Crawlers/prerenderers must always see the language of the URL (unprefixed = Swedish),
// never a language left in localStorage from a previously crawled page.
const _isBot = typeof navigator !== 'undefined'
  && (navigator.webdriver || /bot|crawl|spider|slurp|headless|prerender|lighthouse|ReactSnap/i.test(navigator.userAgent));
if (_isBot) {
  try { localStorage.setItem('i18nextLng', _supportedLangs.includes(_pathLang) ? _pathLang : 'sv'); } catch (e) { /* ignore */ }
}
const _initialLang = _supportedLangs.includes(_pathLang)
  ? _pathLang
  : ((!_isBot && localStorage.getItem('i18nextLng')) || 'sv');

// Initialize with default resources
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    lng: _initialLang, // Sync detection from URL path first, then localStorage
    fallbackLng: 'sv',
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['localStorage', 'querystring', 'navigator'], // localStorage first priority
      lookupQuerystring: 'lang',
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage']
    }
  });

// Load translations from database and update i18n
const loadTranslationsFromDB = async () => {
  try {
    // Dynamic API URL based on current domain
    const getApiUrl = () => {
      if (typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        if (hostname === 'drivemaster.se' || hostname === 'www.drivemaster.se') {
          return 'https://drivemaster.se';
        }
        if (hostname.includes('.emergent.host') || hostname.includes('.emergentagent.com')) {
          return window.location.origin;
        }
      }
      return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    };
    
    const API_URL = getApiUrl();
    const response = await fetch(`${API_URL}/api/public/translations`);
    
    if (response.ok) {
      const data = await response.json();
      const dbTranslations = data.translations;
      
      // Update i18n with database translations (deep merge and overwrite)
      Object.keys(dbTranslations).forEach(lang => {
        // First, get existing resources
        const existingResources = i18n.getResourceBundle(lang, 'translation') || {};
        // Merge with new translations
        const mergedResources = { ...existingResources, ...dbTranslations[lang].translation };
        // Add merged resources back
        i18n.addResourceBundle(lang, 'translation', mergedResources, true, true);
      });
      
      // Check for URL parameter and set language accordingly
      const urlParams = new URLSearchParams(window.location.search);
      const langParam = urlParams.get('lang');
      const savedLang = localStorage.getItem('i18nextLng');
      
      const supportedLanguages = ['ar', 'en', 'sv', 'tr', 'fa', 'so', 'pl', 'es', 'de'];
      
      if (langParam && supportedLanguages.includes(langParam)) {
        // URL parameter takes priority - also save it
        await i18n.changeLanguage(langParam);
        localStorage.setItem('i18nextLng', langParam);
        console.log(`✅ Language set to ${langParam} from URL`);
      } else if (savedLang && supportedLanguages.includes(savedLang)) {
        // Use saved language preference
        await i18n.changeLanguage(savedLang);
        console.log(`✅ Language restored to ${savedLang} from localStorage`);
      } else {
        // Force i18n to reload current language to pick up new translations
        const currentLang = i18n.language;
        await i18n.changeLanguage(currentLang);
      }
      
      console.log('✅ Translations loaded from database');
      
      // Emit event to trigger re-render in React components
      window.dispatchEvent(new Event('translationsLoaded'));
    }
  } catch (error) {
    console.warn('⚠️ Could not load translations from database, using defaults:', error.message);
  }
};

// Load translations on initialization
loadTranslationsFromDB();

// Listen for URL changes to update language
window.addEventListener('popstate', () => {
  const urlParams = new URLSearchParams(window.location.search);
  const langParam = urlParams.get('lang');
  const supportedLanguages = ['ar', 'en', 'sv', 'tr', 'fa', 'so', 'pl', 'es', 'de'];
  if (langParam && supportedLanguages.includes(langParam) && i18n.language !== langParam) {
    i18n.changeLanguage(langParam);
  }
  
  // Also check path-based language (e.g., /ar/, /en/)
  const pathLang = window.location.pathname.split('/')[1];
  if (pathLang && supportedLanguages.includes(pathLang) && i18n.language !== pathLang) {
    i18n.changeLanguage(pathLang);
    localStorage.setItem('i18nextLng', pathLang);
  }
});

// Detect language from path on initial load
const detectLanguageFromPath = () => {
  const supportedLanguages = ['ar', 'en', 'sv', 'tr', 'fa', 'so', 'pl', 'es', 'de'];
  const pathParts = window.location.pathname.split('/').filter(Boolean);
  const pathLang = pathParts[0];
  
  if (pathLang && supportedLanguages.includes(pathLang)) {
    // Path has language prefix like /ar/, /en/, etc.
    i18n.changeLanguage(pathLang);
    localStorage.setItem('i18nextLng', pathLang);
    console.log(`✅ Language set to ${pathLang} from URL path`);
  } else {
    // No language prefix in path - use Swedish as default for / paths
    // But respect localStorage for Swedish pages
    const savedLang = localStorage.getItem('i18nextLng');
    
    // Only use savedLang if we're on a non-language-prefixed route
    // This ensures that visiting / gets Swedish content
    if (!savedLang || savedLang === 'sv') {
      i18n.changeLanguage('sv');
      localStorage.setItem('i18nextLng', 'sv');
    } else if (savedLang && supportedLanguages.includes(savedLang) && window.location.pathname === '/') {
      // User manually visited /, set to Swedish
      i18n.changeLanguage('sv');
      localStorage.setItem('i18nextLng', 'sv');
    }
  }
};

detectLanguageFromPath();

export default i18n;

